import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body2Regular, Body3Regular, Subtitle1Bold, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import Grid, { GridColProps } from '../../components/Grid/Grid';
import ModalAlert from '../../components/modal/ModalAlert';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import SharedMembershipReceivedSection from '../../components/MemberProfile/SharedMembershipReceivedSection';
import {
    selectSharedParticipantEntitlements,
    useMemberSharedEntitlements,
} from '../../features/sharedMembership';
import { usePublicMemberDetail, useUpdatePublicMember, useDeletePublicMember } from '@/features/publicMember/hooks';
import { IFacilitySubscription } from '@/features/facilitySubscription/types';
import { useDeleteSubscription } from '@/features/facilitySubscription/hooks';
import { isMemberRecordReadOnly, isPreservedWithdrawalRecord, PTInfo } from '@/features/publicMember/types';
import SubscriptionRegisterModal from '../../components/FacilitySubscription/SubscriptionRegisterModal';
import SubscriptionRefundModal from '../../components/FacilitySubscription/SubscriptionRefundModal';
import SubscriptionEditModal from '../../components/FacilitySubscription/SubscriptionEditModal';
import SubscriptionTransferModal from '../../components/FacilitySubscription/SubscriptionTransferModal';
import {
    SubscriptionTransferHistoryList,
    SubscriptionTransferHistoryModal,
    TransferKindBadge,
} from '../../components/FacilitySubscription/SubscriptionTransferHistory';
import { buildTransferDoneMessage, getFacilityTransferLocks } from '@/features/facilitySubscription/transfer';
import { hasPendingFacilityTransfer } from '@/features/facilitySubscription/transferPending';
import useSingleQuery from '../../hook/useSingleQuery';
import { fetchAllMembershipForMember, MemberMembershipDataType } from '../../services/useMembershipQueryService';
import MembershipCard from '../../components/MemberProfile/MembershipCard';
import MembershipStartTodayBanner from '../../components/MemberProfile/MembershipStartTodayBanner';
import ModalMembershipCreate from '../../components/MemberProfile/ModalMembershipCreate';
import ModalRegisterPass from '../ClassPass/ModalRegisterPass/ModalRegisterPass';
import NewColorRoundButton from '../../components/Button/NewColorRoundButton';
import MemberClassHistoryComponents from '../../components/MemberProfile/MemberClassHistoryComponents';
import ScheduleDetailModal from '../../components/Schedule/DetailModal/ScheduleDetailModal';
import Modal from '../../components/modal/Modal';
import ModalMemberProfileComponentCoachChange from '../../components/MemberProfile/ModalMemberProfileComponentCoachChange';
import ModalTicketTransfer from '../../components/MemberProfile/ModalTicketTransfer';
import useCenterMutationService from '../../services/useCenterMutationService';
import useMultipleQueries from '../../hook/useMultipleQueries';
import { fetchCoachNameList } from '../../services/useCenterQueryService';
import { patchMemberProfileImageAPI } from '../../api/member';
import { CodeValueType } from '../../types/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { publicMemberKeys } from '@/features/publicMember/keys';
import {
    useAddMemberCoach,
    useMemberCoaches,
    useRemoveMemberCoach,
    useUpdatePrimaryMemberCoach,
} from '@/features/memberCoach/hooks';
import { MemberCoach } from '@/features/memberCoach/types';
import MemberGroupTab from '../../components/GroupSession/MemberGroupTab';
import GroupSessionDetailPopover from '../../components/GroupSession/GroupSessionDetailPopover';
import MemberMergeModal from '../../components/MemberMerge/MemberMergeModal';
import { useMemberTickets } from '@/features/groupTicket/hooks';
import { useMemberBookings } from '@/features/groupBooking/hooks';
import { MemberBooking } from '@/features/groupBooking/types';
import { getNewOpportunitiesAPI } from '../../api/newOpportunity';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { getDailyScheduleAPI } from '../../api/schedule';
import { getGroupSessions } from '@/features/groupSession/api';
import {
    findMatchingGroupSession,
    findMatchingPtSchedule,
    resolveExplicitGroupSessionId,
    resolveGroupSessionId,
    resolveHistoryCoachId,
    resolveHistoryScheduleId,
} from '../../lib/scheduleHistory';
import { formatKoreanPhoneNumber, formatKoreanPhoneNumberInput } from '../../utils/phoneNumber';
import { useAdminVisibleBodyPhotos } from '@/features/bodyPhoto/hooks';
import MemberBodyPhotoTab from '../../components/MemberProfile/MemberBodyPhotoTab';
import MemberDetailTabBar, { MemberDetailTabItem } from '../../components/MemberProfile/MemberDetailTabBar';
import AppAccountStatusBadge from '../../components/MemberList/AppAccountStatusBadge';
import MemberCoachOnboardingChip from '../../components/MemberProfile/MemberCoachOnboardingChip';
import WithdrawnMemberReadOnlyNotice from '../../components/MemberProfile/WithdrawnMemberReadOnlyNotice';
import { appendMembershipRecoveryNotice } from '../../lib/membershipRecovery';
import { ChangeCoachFailure, getChangeCoachErrorCode, getChangeCoachErrorMessage } from '../../lib/changeCoachError';

type NewOpportunityProfileRow = {
    _id?: string;
    id?: string;
    coachName?: string;
    memberName?: string;
    memberPhoneNumber?: string;
    categoryName?: string;
    memo?: string;
    startTime: string;
    endTime: string;
};

const NewOpportunityProfileHistory = ({
    opportunities,
    isLoading,
    renderEmpty,
}: {
    opportunities: NewOpportunityProfileRow[];
    isLoading: boolean;
    renderEmpty: (icon: string, title: string, description: string) => JSX.Element;
}) => {
    if (isLoading) {
        return renderEmpty('◎', '신규기회를 불러오는 중입니다', '회원에게 연결된 신규기회 히스토리를 확인하고 있습니다.');
    }

    if (!opportunities.length) {
        return renderEmpty('◎', '신규기회가 없습니다', '이 회원에게 아직 연결된 신규기회가 없습니다.');
    }

    return (
        <Col style={{ gap: 12 }}>
            {opportunities.map((item) => {
                return (
                    <div
                        key={item._id || item.id || `${item.startTime}-${item.categoryName}`}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '132px 1fr',
                            gap: 18,
                            alignItems: 'flex-start',
                            padding: '18px 20px',
                            border: '1px solid #E5E7EB',
                            borderRadius: 12,
                            background: '#FFFFFF',
                            boxShadow: '0 1px 3px rgba(17, 24, 39, 0.04)',
                        }}
                    >
                        <Col>
                            <span style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>
                                {dayjs(item.startTime).format('YYYY.MM.DD')}
                            </span>
                            <span style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: '#6B7280' }}>
                                {dayjs(item.startTime).format('HH:mm')} - {dayjs(item.endTime).format('HH:mm')}
                            </span>
                        </Col>
                        <Col>
                            <Row style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span
                                    style={{
                                        padding: '5px 9px',
                                        borderRadius: 999,
                                        background: '#F5F3FF',
                                        color: '#6D28D9',
                                        fontSize: 12,
                                        fontWeight: 800,
                                    }}
                                >
                                    {item.categoryName || '신규기회'}
                                </span>
                                <span style={{ fontSize: 13, color: '#374151', fontWeight: 700 }}>
                                    {item.coachName || '-'} 강사
                                </span>
                            </Row>
                            <span style={{ marginTop: 10, fontSize: 14, color: '#374151', lineHeight: '21px' }}>
                                {item.memo || '메모 없음'}
                            </span>
                        </Col>
                    </div>
                );
            })}
        </Col>
    );
};

const CoachPTSection = ({
    relation,
    memberId,
    memberName,
    createAlert,
    hideCoachLabel = false,
    isReadOnly = false,
}: {
    relation: any;
    memberId: string;
    memberName?: string;
    createAlert: any;
    hideCoachLabel?: boolean;
    isReadOnly?: boolean;
}) => {
    const navigate = useNavigate();
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isRegisterPassModal, setIsRegisterPassModal] = useState(false);

    const {
        refinedResult: asyncResult,
        rawData: rawMembershipResponse,
        isFetching: isMembershipFetching,
        refresh: refetchMemberships,
    } = useSingleQuery({
        fetchFn: fetchAllMembershipForMember,
        params: {
            coachId: relation.coachId,
            memberId: memberId,
            accessToken: authObject?.accessToken,
        },
        enabled: true,
        suspense: false,
    });

    // 시작일 변경 뒤 재조회에 실패해도 마지막 정상 카드와 재시도 배너를 유지한다.
    const lastMemberships = useRef<{ relationKey: string; data: MemberMembershipDataType[]; received: boolean }>({
        relationKey: '',
        data: [],
        received: false,
    });
    const membershipResponse = rawMembershipResponse as {
        status?: number;
        data?: { membersMemberships?: MemberMembershipDataType[] };
    } | undefined;
    const relationKey = `${relation.coachId}:${memberId}`;
    if (lastMemberships.current.relationKey !== relationKey) {
        lastMemberships.current = { relationKey, data: [], received: false };
    }
    const responseMemberships = membershipResponse?.data?.membersMemberships;
    if (!asyncResult?.isPreviousData && membershipResponse?.status === 200 && Array.isArray(responseMemberships)) {
        lastMemberships.current.data = responseMemberships;
        lastMemberships.current.received = true;
    }
    const memberships = lastMemberships.current.data;
    /**
     * 정상 목록을 한 번도 받지 못한 상태에서는 빈 목록을 "수강권 없음"으로 그리지 않는다.
     * 빈 목록 옆의 "수강권 발급하기" 카드를 보고 한 장 더 발급하는 중복 발급이 실제로 있었다.
     *  - 첫 로딩 중: 불러오는 중 안내.
     *  - 조회 실패(네트워크 오류로 예외, 502·504 처럼 api 계층이 아무것도 돌려주지 않은 경우):
     *    "불러오지 못했어요" + 다시 불러오기.
     * api 계층이 400·401·403·404·500 을 `{ status }` 로 바꿔 돌려주는 기존 동작은 이번 범위 밖이라
     * 예전처럼 빈 목록으로 둔다. 한 번 받은 정상 목록은 재조회가 실패해도 그대로 유지한다.
     */
    const hasReceivedMemberships = lastMemberships.current.received;
    const API_EMPTY_STATUSES = [400, 401, 403, 404, 500];
    const isMembershipLoadFailed =
        !hasReceivedMemberships &&
        !isMembershipFetching &&
        (asyncResult?.isError === true ||
            (asyncResult?.isSuccess === true &&
                (membershipResponse === undefined ||
                    (membershipResponse?.status !== 200 &&
                        !API_EMPTY_STATUSES.includes(Number(membershipResponse?.status))))));
    const isMembershipFirstLoading = !hasReceivedMemberships && !isMembershipLoadFailed && isMembershipFetching;
    const isMembershipListUnsettled = isMembershipFirstLoading || isMembershipLoadFailed;
    const moveToClassRegister = (membership?: MemberMembershipDataType) => {
        if (!relation.coachId) {
            createAlert('담당 강사 확인 필요', '수강권의 담당 강사 정보를 찾을 수 없습니다.', true);
            return;
        }

        const selectedSchedule = {
            memberId,
            coachId: membership?.coachId || relation.coachId,
            coachName: membership?.coachName || relation.coachName || relation.alias,
            ...(membership?._id ? { membershipId: membership._id } : {}),
            scheduleStartTime: dayjs().format('YYYY-MM-DD HH:mm'),
        };
        navigate('/schedule/create', {
            state: {
                type: 'oneSchedule',
                selectedSchedule,
                calendarType: 'daily',
                calendarSelectedDay: dayjs().format('YYYY-MM-DD'),
                calendarScrollY: 0,
                returnPath: `/member/${memberId}`,
                returnState: { memberId },
            },
        });
    };

    return (
        <Col style={{ marginBottom: 32 }}>
            <Row
                className="items-center"
                style={{ marginBottom: 12, justifyContent: hideCoachLabel ? 'flex-end' : 'space-between' }}
            >
                {!hideCoachLabel && (
                    <Row className="items-center gap-[8px]">
                        <span style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>
                            담당 강사: {relation.coachName || relation.alias || relation.coachId}
                        </span>
                    </Row>
                )}
                {!isReadOnly && <Row className="items-center gap-[8px]">
                    <button
                        onClick={() => moveToClassRegister()}
                        style={{
                            padding: '6px 14px',
                            height: 32,
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: '#FFFFFF',
                            color: '#3B82F6',
                            border: '1px solid #BFDBFE',
                            cursor: 'pointer',
                        }}
                    >
                        수업 등록 바로가기 →
                    </button>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        style={{
                            padding: '6px 14px',
                            height: 32,
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            border: '1px solid #3B82F6',
                            cursor: 'pointer',
                        }}
                    >
                        + 수강권 발급
                    </button>
                </Row>}
            </Row>

            {!isReadOnly && (
                <MembershipStartTodayBanner memberId={memberId} coachId={relation.coachId} memberships={memberships} />
            )}
            <Row
                style={{
                    overflowX: 'auto',
                    paddingBottom: 16,
                    gap: 16,
                }}
            >
                {memberships.map((membership, idx) => (
                    <MembershipCard
                        key={membership._id}
                        index={idx}
                        membership={membership}
                        onClickDetailPage={() =>
                            navigate('/member/detail/membership', {
                                state: {
                                    membershipId: membership._id,
                                    memberName: memberName,
                                    coachName: membership.coachName || relation.coachName || relation.alias,
                                    memberId: memberId,
                                    coachId: membership.coachId || relation.coachId,
                                    isReadOnly,
                                },
                            })
                        }
                        onClickRegisterSchedule={isReadOnly ? undefined : (membership) => moveToClassRegister(membership)}
                    />
                ))}
                {isMembershipFirstLoading && (
                    <Col
                        role="status"
                        style={{
                            width: 310,
                            height: 160,
                            border: '1px solid #E5E7EB',
                            backgroundColor: '#F9FAFB',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: 3,
                            fontSize: 13,
                            color: '#6B7280',
                        }}
                    >
                        수강권을 불러오는 중이에요
                    </Col>
                )}
                {isMembershipLoadFailed && (
                    <Col
                        role="alert"
                        style={{
                            width: 310,
                            height: 160,
                            border: '1px solid #FECACA',
                            backgroundColor: '#FEF2F2',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 12,
                            flexShrink: 0,
                            marginTop: 3,
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#374151' }}>수강권을 불러오지 못했어요</span>
                        <button
                            type="button"
                            onClick={() => refetchMemberships()}
                            style={{
                                padding: '6px 14px',
                                height: 32,
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 600,
                                backgroundColor: '#FFFFFF',
                                color: '#3B82F6',
                                border: '1px solid #BFDBFE',
                                cursor: 'pointer',
                            }}
                        >
                            다시 불러오기
                        </button>
                    </Col>
                )}
                {!isReadOnly && !isMembershipListUnsettled && memberships.length <= 2 && (
                    <Col
                        style={{
                            width: 310,
                            height: 160,
                            border: '1px dashed #BFDBFE',
                            backgroundColor: '#EFF6FF',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            marginTop: 3,
                        }}
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        <NewColorRoundButton
                            text="수강권 발급하기"
                            fontType="CaptionRegular"
                            width={112}
                            height={24}
                            bgColor="primary"
                            textColor="white"
                        />
                    </Col>
                )}
            </Row>

            {!isReadOnly && isCreateModalOpen && (
                <ModalMembershipCreate
                    setIsVisible={setIsCreateModalOpen}
                    onClickCreateNewMembership={() => setIsRegisterPassModal(true)}
                    memberName={memberName}
                    coachName={relation.coachName || relation.alias}
                    coachId={relation.coachId}
                    memberId={memberId}
                    createAlert={createAlert}
                />
            )}
            {!isReadOnly && isRegisterPassModal && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModal}
                    setModalVisible={setIsRegisterPassModal}
                    createAlert={createAlert}
                />
            )}
        </Col>
    );
};

/* ═══════ PT 탭 (다중 강사) ═══════ */

const PTTab = ({
    ptInfo,
    memberId,
    memberName,
    centerId,
    dropDownCoachList,
    createAlert,
    createTwoButtonAlert,
    isReadOnly = false,
}: {
    ptInfo: PTInfo | undefined;
    memberId: string;
    memberName?: string;
    centerId: string;
    dropDownCoachList: CodeValueType[];
    createAlert: any;
    createTwoButtonAlert?: any;
    isReadOnly?: boolean;
}) => {
    const [showAddCoach, setShowAddCoach] = useState(false);
    const [selectedCoachId, setSelectedCoachId] = useState('');
    const { data: assignedCoaches, isFetched: isAssignedCoachesFetched } = useMemberCoaches({
        memberId,
        centerId,
    });
    const addCoachMutation = useAddMemberCoach();
    const removeCoachMutation = useRemoveMemberCoach();
    const updatePrimaryCoachMutation = useUpdatePrimaryMemberCoach();

    const allRelations = isAssignedCoachesFetched && assignedCoaches
        ? assignedCoaches
        : ((ptInfo?.relations ?? []) as MemberCoach[]);

    // 이미 배정된 강사 ID 목록
    const assignedCoachIds = new Set(allRelations.map((c) => c.coachId));

    // 공유받은 수강권 카드에 담당 강사를 표시하기 위한 이름 맵.
    const coachNameById = Object.fromEntries(
        allRelations.map((relation: any) => [
            String(relation.coachId),
            relation.coachName || relation.alias || '',
        ]),
    );

    // 배정 가능한 강사 (이미 배정된 강사 제외)
    const availableCoaches = dropDownCoachList.filter((c) => !assignedCoachIds.has(c.code));

    const handleAddCoach = () => {
        if (!selectedCoachId) return;
        addCoachMutation.mutate(
            { memberId, coachId: selectedCoachId, centerId },
            {
                onSuccess: () => {
                    const coachName = dropDownCoachList.find((c) => c.code === selectedCoachId)?.value || '';
                    createAlert('배정 완료', `${coachName} 강사가 배정되었습니다.`, false);
                    setSelectedCoachId('');
                    setShowAddCoach(false);
                },
                onError: () => {
                    createAlert('배정 실패', '강사 배정에 실패했습니다.', true);
                },
            }
        );
    };

    return (
        <Col className="gap-[20px]">
            {/* 배정된 강사 목록 */}
            <Col>
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>
                        배정된 강사 ({allRelations.length}명)
                    </span>
                    {!isReadOnly && <button
                        onClick={() => setShowAddCoach(!showAddCoach)}
                        style={{
                            padding: '6px 16px',
                            borderRadius: 8,
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        + 강사 추가 배정
                    </button>}
                </Row>

                {/* 강사 추가 UI */}
                {!isReadOnly && showAddCoach && (
                    <Row style={{
                        padding: 16,
                        backgroundColor: '#F8FAFF',
                        borderRadius: 10,
                        border: '1px solid #DBEAFE',
                        marginBottom: 12,
                        alignItems: 'center',
                        gap: 10,
                    }}>
                        <select
                            value={selectedCoachId}
                            onChange={(e) => setSelectedCoachId(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '10px 14px',
                                borderRadius: 8,
                                border: '1px solid #D1D5DB',
                                fontSize: 14,
                            }}
                        >
                            <option value="">강사를 선택하세요</option>
                            {availableCoaches.map((c) => (
                                <option key={c.code} value={c.code}>{c.value}</option>
                            ))}
                        </select>
                        <button
                            onClick={handleAddCoach}
                            disabled={!selectedCoachId || addCoachMutation.isLoading}
                            style={{
                                padding: '10px 20px',
                                borderRadius: 8,
                                fontSize: 14,
                                fontWeight: 600,
                                backgroundColor: selectedCoachId ? '#3B82F6' : '#D1D5DB',
                                color: 'white',
                                border: 'none',
                                cursor: selectedCoachId ? 'pointer' : 'default',
                            }}
                        >
                            {addCoachMutation.isLoading ? '배정 중...' : '배정'}
                        </button>
                        <button
                            onClick={() => { setShowAddCoach(false); setSelectedCoachId(''); }}
                            style={{
                                padding: '10px 14px',
                                borderRadius: 8,
                                fontSize: 14,
                                backgroundColor: 'white',
                                color: '#6B7280',
                                border: '1px solid #D1D5DB',
                                cursor: 'pointer',
                            }}
                        >
                            취소
                        </button>
                    </Row>
                )}

                {allRelations.length > 0 && (
                    <Col style={{ gap: 10 }}>
                        {allRelations.map((coach) => (
                            <Row
                                key={coach.coachId}
                                style={{
                                    padding: 16,
                                    borderRadius: 10,
                                    border: `1px solid ${coach.isPrimary ? '#BFDBFE' : '#E5E7EB'}`,
                                    backgroundColor: coach.isPrimary ? '#F8FBFF' : '#FFFFFF',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                }}
                            >
                                <Col style={{ gap: 6 }}>
                                    <Row className="items-center gap-[8px]" style={{ flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                                            {coach.coachName || coach.alias || coach.coachId}
                                        </span>
                                        {coach.isPrimary && (
                                            <span
                                                style={{
                                                    padding: '3px 8px',
                                                    borderRadius: 999,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    backgroundColor: '#DBEAFE',
                                                    color: '#1D4ED8',
                                                }}
                                            >
                                                대표 강사
                                            </span>
                                        )}
                                        <MemberCoachOnboardingChip
                                            relation={ptInfo?.relations?.find((relation) => relation.coachId === coach.coachId)}
                                        />
                                        {coach.centerName && (
                                            <span style={{ fontSize: 12, color: '#6B7280' }}>
                                                {coach.centerName}
                                            </span>
                                        )}
                                    </Row>
                                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                                        {coach.canRequest === false ? '요청 제한' : '요청 가능'}
                                    </span>
                                </Col>
                                {!isReadOnly && <Row className="items-center gap-[8px]" style={{ flexWrap: 'wrap' }}>
                                    {!coach.isPrimary && (
                                        <button
                                            onClick={() =>
                                                updatePrimaryCoachMutation.mutate(
                                                    { memberId, coachId: coach.coachId, centerId },
                                                    {
                                                        onSuccess: () => {
                                                            createAlert('대표 강사 변경 완료', `${coach.coachName} 강사님이 대표 강사로 설정되었습니다.`, false);
                                                        },
                                                        onError: () => {
                                                            createAlert('대표 강사 변경 실패', '대표 강사 변경에 실패했습니다.', true);
                                                        },
                                                    },
                                                )
                                            }
                                            disabled={updatePrimaryCoachMutation.isLoading || removeCoachMutation.isLoading}
                                            style={{
                                                padding: '8px 12px',
                                                borderRadius: 8,
                                                fontSize: 13,
                                                fontWeight: 600,
                                                backgroundColor: updatePrimaryCoachMutation.isLoading ? '#DBEAFE' : '#EFF6FF',
                                                color: '#2563EB',
                                                border: '1px solid #BFDBFE',
                                                cursor: (updatePrimaryCoachMutation.isLoading || removeCoachMutation.isLoading) ? 'not-allowed' : 'pointer',
                                                opacity: (updatePrimaryCoachMutation.isLoading || removeCoachMutation.isLoading) ? 0.6 : 1,
                                            }}
                                        >
                                            {updatePrimaryCoachMutation.isLoading ? '변경 중...' : '대표로 설정'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            if (removeCoachMutation.isLoading || updatePrimaryCoachMutation.isLoading) return;
                                            const doRemove = () =>
                                                removeCoachMutation.mutate(
                                                    { memberId, coachId: coach.coachId, centerId },
                                                    {
                                                        onSuccess: () => {
                                                            createAlert('연결 해제 완료', `${coach.coachName} 강사 연결이 해제되었습니다.`, false);
                                                        },
                                                        onError: () => {
                                                            createAlert('연결 해제 실패', '강사 연결 해제에 실패했습니다.', true);
                                                        },
                                                    },
                                                );
                                            const coachLabel = coach.coachName || coach.alias || '해당';
                                            const warnMessage =
                                                `${coachLabel} 강사님과 ${memberName ?? '회원'}님의 연결을 해제하시겠습니까?\n\n` +
                                                `연결 해제 시 해당 강사의 수강권/수업 요청이 제한될 수 있으며,\n` +
                                                `이 작업은 되돌릴 수 없습니다.` +
                                                (coach.isPrimary ? `\n\n⚠️ 이 강사는 대표 강사로 지정되어 있습니다.` : '');
                                            if (createTwoButtonAlert) {
                                                createTwoButtonAlert(
                                                    '강사 연결을 해제하시겠습니까?',
                                                    warnMessage,
                                                    true,
                                                    '취소',
                                                    '연결 해제',
                                                    () => {},
                                                    () => {
                                                        doRemove();
                                                    },
                                                );
                                            } else {
                                                doRemove();
                                            }
                                        }}
                                        disabled={removeCoachMutation.isLoading || updatePrimaryCoachMutation.isLoading}
                                        style={{
                                            padding: '8px 12px',
                                            borderRadius: 8,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            backgroundColor: '#FFFFFF',
                                            color: '#DC2626',
                                            border: '1px solid #FCA5A5',
                                            cursor: (removeCoachMutation.isLoading || updatePrimaryCoachMutation.isLoading) ? 'not-allowed' : 'pointer',
                                            opacity: (removeCoachMutation.isLoading || updatePrimaryCoachMutation.isLoading) ? 0.6 : 1,
                                        }}
                                    >
                                        {removeCoachMutation.isLoading ? '해제 중...' : '연결 해제'}
                                    </button>
                                </Row>}
                            </Row>
                        ))}
                    </Col>
                )}

            </Col>

            {/* 수강권 상태 뱃지 */}
            {ptInfo && (
                <Row className="items-center gap-[8px]">
                    <span style={{
                        padding: '4px 12px',
                        borderRadius: 8,
                        fontSize: 15,
                        fontWeight: 600,
                        backgroundColor: ptInfo.hasActivePT ? '#D1FAE5' : '#F3F4F6',
                        color: ptInfo.hasActivePT ? '#065F46' : '#6B7280',
                    }}>
                        {ptInfo.hasActivePT ? '● 수강권 진행중' : '○ 수강권 없음'}
                    </span>
                </Row>
            )}

            {/* 기존 강사별 수강권 카드 */}
            {allRelations.length > 0 && (
                <Col className="gap-[8px]">
                    {allRelations.map((rel: any, i: number) => (
                        <CoachPTSection
                            key={i}
                            relation={rel}
                            memberId={memberId}
                            memberName={memberName}
                            createAlert={createAlert}
                            hideCoachLabel={allRelations.length === 1}
                            isReadOnly={isReadOnly}
                        />
                    ))}
                </Col>
            )}

            {/* 강사 없는 경우 */}
            {allRelations.length === 0 && (
                <Col style={{ alignItems: 'center', padding: '48px 0' }}>
                    <span style={{ fontSize: 44, marginBottom: 16 }}>👨‍🏫</span>
                    <span style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                        담당 강사가 배정되지 않았습니다
                    </span>
                    <span style={{ fontSize: 14, color: '#9CA3AF', marginBottom: 24 }}>
                        위의 "강사 추가 배정" 버튼으로 강사를 배정해주세요.
                    </span>
                </Col>
            )}

            {/* 공유받은 수강권은 본인 소유 문서가 없어 위 담당 강사별 목록에 나오지 않는다. */}
            <SharedMembershipReceivedSection
                memberId={memberId}
                centerId={centerId}
                coachNameById={coachNameById}
                isReadOnly={isReadOnly}
            />
        </Col>
    );
};

type Tab = 'history' | 'bodyPhoto' | 'newOpportunity' | 'subscription' | 'pt' | 'group' | 'checkin';

const MEMBER_TYPE_HISTORY_LABEL: Record<string, string> = {
    public: '퍼블릭 회원',
    temp: '미연동 수강 회원',
    original: '연동 수강 회원',
};

const MEMBER_TYPE_CHANGE_REASON_LABEL: Record<string, string> = {
    coach_assignment: '강사 배정',
    coach_deletion: '담당 강사 삭제',
    pt_register: 'PT 등록',
    app_onboarding: '앱 연동 완료',
    admin_reclassify: '관리자 직접 변경',
    excel_import: '엑셀 등록',
    kiosk_visit: '키오스크 방문 등록',
    unknown: '기타',
};

const getMemberTypeHistoryLabel = (memberType?: string) => {
    if (!memberType) return '알 수 없음';
    return MEMBER_TYPE_HISTORY_LABEL[memberType] || memberType;
};

const getMemberTypeChangeReasonLabel = (reason?: string) => {
    if (!reason) return '';
    if (MEMBER_TYPE_CHANGE_REASON_LABEL[reason]) {
        return MEMBER_TYPE_CHANGE_REASON_LABEL[reason];
    }
    if (/^[a-z0-9_]+$/i.test(reason)) {
        return '기타';
    }
    return reason;
};

const MEMBER_TYPE_COLOR: Record<string, { bg: string; color: string }> = {
    public: { bg: '#EFF6FF', color: '#2563EB' },
    temp: { bg: '#FFF7ED', color: '#EA580C' },
    original: { bg: '#F0FDF4', color: '#16A34A' },
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; dot: string }> = {
    active: { label: '활성', bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
    inactive: { label: '비활성', bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' },
    suspended: { label: '정지', bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
    expired: { label: '만료', bg: '#FEE2E2', color: '#DC2626', dot: '#EF4444' },
    empty: { label: '대기', bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
    none: { label: '없음', bg: '#F3F4F6', color: '#9CA3AF', dot: '#D1D5DB' },
};

const PublicMemberDetailPage = ({ createAlert, createTwoButtonAlert }: { createAlert: any; createTwoButtonAlert?: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const centerId = authObject?.centerId;
    const isCenterViewer = authObject?.accessMode === 'view' || authObject?.role === 'center_viewer';
    const canViewBodyPhotos = !isCenterViewer;
    const { memberId } = useParams<{ memberId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const profileImageInputRef = useRef<HTMLInputElement>(null);

    // 탭 상태는 localStorage에 저장 — 다른 화면 갔다가 돌아와도 마지막 탭 복원
    const TAB_STORAGE_KEY = 'memberProfile.activeTab';
    const [activeTab, setActiveTab] = useState<Tab>(() => {
        const saved = typeof window !== 'undefined' ? localStorage.getItem(TAB_STORAGE_KEY) : null;
        const allowed: Tab[] = [
            'history',
            ...(canViewBodyPhotos ? (['bodyPhoto'] as Tab[]) : []),
            'newOpportunity',
            'pt',
            'subscription',
            'group',
            'checkin',
        ];
        return (saved && (allowed as string[]).includes(saved) ? (saved as Tab) : 'history');
    });
    useEffect(() => {
        try { localStorage.setItem(TAB_STORAGE_KEY, activeTab); } catch {}
    }, [activeTab]);
    useEffect(() => {
        if (!canViewBodyPhotos && activeTab === 'bodyPhoto') setActiveTab('history');
    }, [activeTab, canViewBodyPhotos]);
    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [isSubscriptionModalVisible, setIsSubscriptionModalVisible] = useState(false);
    const [refundTarget, setRefundTarget] = useState<IFacilitySubscription | null>(null);
    const [editTarget, setEditTarget] = useState<IFacilitySubscription | null>(null);
    const [selectedSubscription, setSelectedSubscription] = useState<IFacilitySubscription | null>(null);
    const [transferTarget, setTransferTarget] = useState<IFacilitySubscription | null>(null);
    const [isTransferHistoryVisible, setIsTransferHistoryVisible] = useState(false);
    const deleteMutationSub = useDeleteSubscription();
    const [deleteSubTarget, setDeleteSubTarget] = useState<{ sub: IFacilitySubscription; isRefunded: boolean } | null>(null);
    const [isChangeCoachModal, setIsChangeCoachModal] = useState(false);
    // 실패 응답이 오는 시점에 모달이 열려 있는지. Modal 의 Esc·바깥 클릭은 요청 중에도 닫을 수 있어
    // 닫힌 뒤 실패가 오면 카드가 보이지 않으므로, 그때는 알림으로 대신 알린다.
    const isChangeCoachModalRef = useRef(false);
    useEffect(() => {
        isChangeCoachModalRef.current = isChangeCoachModal;
    }, [isChangeCoachModal]);
    const [changeCoachFailure, setChangeCoachFailure] = useState<{
        failure: ChangeCoachFailure;
        occurredAt: Date;
    } | null>(null);
    const [isAddCoachModal, setIsAddCoachModal] = useState(false);
    const [coachIdToAdd, setCoachIdToAdd] = useState('');
    const [isTicketTransferModal, setIsTicketTransferModal] = useState(false);

    // 그룹수업 수강권 활성 개수 — 탭 콘텐츠(MemberGroupTab)와 같은 기준이어야 한다.
    // includeShared 없이 세면 공유받은 티켓만 가진 참여자가 "그룹 수업 0"으로 보인다.
    const { data: groupMemberTickets = [] } = useMemberTickets({ centerId: centerId || '', memberId: memberId || '', status: 'ACTIVE', includeShared: true, enabled: !!centerId && !!memberId });
    const activeGroupTicketCount = (groupMemberTickets as any[]).length;

    // 공유받은 PT 수강권 개수 — 소유 목록(강사별 조회)에는 없어 별도 합산해야
    // 탭/스탯 카운트가 아래 '공유받은 수강권' 섹션과 일치한다.
    const { isSharedMembershipReadEnabled } = useCenterFeatureFlags();
    const sharedPtEntitlementsQuery = useMemberSharedEntitlements({
        memberId: memberId || '',
        centerId: centerId || '',
        enabled: isSharedMembershipReadEnabled && !!centerId && !!memberId,
    });
    const sharedPtReceivedCount = selectSharedParticipantEntitlements(
        sharedPtEntitlementsQuery.data,
    ).length;

    const [isVisibleScheduleDetail, setIsVisibleScheduleDetail] = useState(false);
    const [detailObject, setDetailObject] = useState<any>();
    // 그룹 수업 기록 row 클릭 시 띄울 세션 popover
    const [groupSessionPopoverId, setGroupSessionPopoverId] = useState<string | null>(null);

    // 강사 목록 조회 (담당 강사 변경용)
    const { refinedResults: coachQueryResults } = useMultipleQueries([
        {
            fetchFn: fetchCoachNameList,
            params: { centerId: authObject?.centerId, isAllConcat: true },
            enabled: !!authObject?.centerId,
        },
    ]);
    const dropDownCoachList = useMemo(
        () => (coachQueryResults?.[0]?.data as CodeValueType<string>[]) ?? [],
        [coachQueryResults],
    );

    // 담당 강사 변경 mutation
    const { patchChangeCoachForMemberMutation, postAssignCoachMutation } = useCenterMutationService({
        onPatchChangeCoachForMemberSuccess: (data: any) => {
            const newCoachName = data?.newCoachName;
            setChangeCoachFailure(null);
            setIsChangeCoachModal(false);
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(centerId, memberId || '') });
            createAlert(
                '담당 강사 변경 완료',
                // 재연결이 되살리지 못한 수강권이 있으면 함께 알린다(U9). 환불 진행 중이거나
                // 인계된 건은 서버가 의도적으로 건너뛴다.
                appendMembershipRecoveryNotice(
                    `담당 강사가 ${newCoachName || ''} 강사님으로 변경되었습니다.`,
                    data
                ),
                false
            );
        },
        onPatchChangeCoachForMemberError: (error: ChangeCoachFailure) => {
            // 이미 다른 강사로 바뀌었거나 공유·환불로 막힌 경우 화면이 낡아 있을 수 있어 최신 상세를 다시 받는다.
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(centerId, memberId || '') });
            if (!isChangeCoachModalRef.current) {
                createAlert('변경 실패', getChangeCoachErrorMessage(error), true);
                return;
            }
            // 선택값을 유지한 채 모달 안에서 원인과 다음 행동을 안내한다.
            setChangeCoachFailure({ failure: error, occurredAt: new Date() });
        },
        onPostAssignCoachSuccess: (data: any) => {
            setIsChangeCoachModal(false);
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(centerId, memberId || '') });
            createAlert(
                '배정 완료',
                appendMembershipRecoveryNotice('담당 강사 배정에 성공했습니다.', data),
                false
            );
        },
        onPostAssignCoachError: () => {
            createAlert('배정 실패', '담당 강사 배정에 실패했습니다.', true);
        },
    });

    // 프로필 사진 업로드
    const handleProfileImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !centerId || !memberId) return;
        const reader = new FileReader();
        reader.onload = async () => {
            const base64 = reader.result as string;
            try {
                const res = await patchMemberProfileImageAPI(centerId, memberId, base64, authorization);
                if (res?.status === 200) {
                    await Promise.all([
                        queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(centerId, memberId) }),
                        queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(centerId) }),
                        queryClient.invalidateQueries({ queryKey: ['getCenterMemberList'] }),
                        queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
                        queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
                    ]);
                    createAlert('업로드 완료', '프로필 사진이 변경되었습니다.', false);
                } else {
                    createAlert('업로드 실패', '프로필 사진 업로드에 실패했습니다.', true);
                }
            } catch {
                createAlert('업로드 실패', '프로필 사진 업로드에 실패했습니다.', true);
            }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };
    
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editGender, setEditGender] = useState<'male' | 'female' | ''>('');
    const [editBirthDate, setEditBirthDate] = useState('');
    const [editMemo, setEditMemo] = useState('');
    const [editAddress, setEditAddress] = useState('');

    const {
        data: memberDetail,
        refetch,
        isLoading: loadingDetail,
        isError: isDetailError,
        error: detailError,
    } = usePublicMemberDetail({
        centerId,
        memberId: memberId || '',
    });
    const bodyPhotoQuery = useAdminVisibleBodyPhotos({
        centerId: centerId || '',
        memberId: memberId || '',
        enabled: canViewBodyPhotos && activeTab === 'bodyPhoto',
    });
    const { isNewOpportunityEnabled } = useCenterFeatureFlags();
    const newOpportunityQuery = useQuery({
        queryKey: ['memberProfileNewOpportunities', centerId, memberId],
        queryFn: () =>
            getNewOpportunitiesAPI(centerId || '', authorization, {
                memberId: memberId || '',
                includeActiveMembers: 'true',
                limit: '100',
            }),
        enabled: isNewOpportunityEnabled && !!centerId && !!authorization && !!memberId,
    });
    const memberNewOpportunities: NewOpportunityProfileRow[] = newOpportunityQuery.data?.data?.results || [];
    const hasMemberNewOpportunities = memberNewOpportunities.length > 0;
    useEffect(() => {
        if (
            activeTab === 'newOpportunity' &&
            (!isNewOpportunityEnabled || (!newOpportunityQuery.isLoading && !hasMemberNewOpportunities))
        ) {
            setActiveTab('history');
        }
    }, [activeTab, hasMemberNewOpportunities, isNewOpportunityEnabled, newOpportunityQuery.isLoading]);
    const { data: assignedCoaches, isFetched: isAssignedCoachesFetched } = useMemberCoaches({
        memberId: memberId || '',
        centerId,
        enabled: !!memberId,
    });
    const addCoachMutation = useAddMemberCoach();

    const updateMutation = useUpdatePublicMember();
    const deleteMutation = useDeletePublicMember();

    const member = memberDetail?.centerMember;
    /**
     * 회원 조회 실패를 화면에 드러내기 위한 판정.
     *
     * 예전에는 404를 조용히 삼켜서 "이용권 0건"인 정상 화면처럼 보였고,
     * member 가 undefined 라 isMemberRecordReadOnly(undefined) === false 가 되어
     * '+ 이용권 등록' 같은 쓰기 버튼이 전부 살아 있었다. 그래서 삭제된 회원 문서에
     * 새 이용권이 붙는 경로가 열려 있었다.
     *
     * isMemberRecordReadOnly 는 다른 화면(정산/신규기회/공유 수강권 등)에서도 쓰는 공용
     * 헬퍼라 시그니처를 건드리지 않고, 이 화면에서 렌더 자체를 막는 방식으로 처리한다.
     */
    const detailErrorStatus = (detailError as { status?: number } | null | undefined)?.status;
    const isMemberNotFound = isDetailError && detailErrorStatus === 404;
    // 404 는 회원이 없다는 확정 신호라 캐시가 남아 있어도 무조건 차단한다.
    // (다른 탭에서 삭제된 뒤 refetch 가 404 로 돌아오는 경우가 여기 걸린다.)
    // 반면 500/네트워크 오류는 조회 결과가 아예 없을 때만 차단한다. 여기에 isDetailError 를
    // 그대로 넣으면, 이미 정상 조회한 화면에서 배경 refetch 가 한 번 실패했을 때
    // 멀쩡한 회원 페이지가 통째로 에러 화면으로 바뀐다.
    const isMemberDetailUnavailable = !loadingDetail && (isMemberNotFound || !memberDetail);
    const isReadOnly = isMemberRecordReadOnly(member);
    const canRemovePreservedMember = isPreservedWithdrawalRecord(member);
    /**
     * 주소·키·시작 체중. 신서버는 centerMember 와 member 양쪽에 내려주고, 구서버는 키 자체가 없다.
     * 주소 키가 없는 응답(구서버)에서는 수정 폼의 주소 입력란을 숨긴다 — 구서버 Joi 는
     * 모르는 키를 400 으로 거절하므로, 입력란이 보이면 저장이 확정적으로 실패한다.
     */
    const isAddressSupported =
        typeof member?.address === 'string' || typeof memberDetail?.member?.address === 'string';
    const profileAddress = (member?.address || memberDetail?.member?.address || '').trim();
    const toPositiveNumber = (value: number | null | undefined) =>
        typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
    const profileHeight = toPositiveNumber(member?.height ?? memberDetail?.member?.height);
    const profileStartWeight = toPositiveNumber(member?.startWeight ?? memberDetail?.member?.startWeight);
    const ptInfo: PTInfo | undefined = memberDetail?.ptInfo;
    const resolvedRelations = useMemo(
        () =>
            isAssignedCoachesFetched
                ? assignedCoaches ?? []
                : ((ptInfo?.relations ?? []) as MemberCoach[]),
        [assignedCoaches, isAssignedCoachesFetched, ptInfo?.relations],
    );
    const onboardingPending = (ptInfo?.relations ?? []).some((relation) => relation.onboardingPending === true);
    const onboardingStale = (ptInfo?.relations ?? []).some(
        (relation) => relation.onboardingPending === true && relation.onboardingStale === true,
    );
    const primaryRelation = useMemo(
        () => resolvedRelations.find((rel) => rel.isPrimary) || resolvedRelations[0],
        [resolvedRelations],
    );
    const assignedCoachOptions = useMemo<CodeValueType<string>[]>(
        () =>
            resolvedRelations
                .filter((rel) => !!rel.coachId)
                .map((rel) => ({
                    code: rel.coachId,
                    value: rel.coachName || rel.alias || '이름 없는 강사',
                    object: rel,
                })),
        [resolvedRelations],
    );
    const availableCoachOptions = useMemo(() => {
        const assignedCoachIds = new Set(resolvedRelations.map((relation) => relation.coachId));
        return dropDownCoachList.filter((coach) => !assignedCoachIds.has(coach.code));
    }, [dropDownCoachList, resolvedRelations]);

    const closeAddCoachModal = () => {
        if (addCoachMutation.isLoading) return;
        setIsAddCoachModal(false);
        setCoachIdToAdd('');
    };

    const handleAddCoach = () => {
        if (!memberId || !centerId || !coachIdToAdd) return;
        const coachName = availableCoachOptions.find((coach) => coach.code === coachIdToAdd)?.value || '';

        addCoachMutation.mutate(
            { memberId, coachId: coachIdToAdd, centerId },
            {
                onSuccess: () => {
                    setIsAddCoachModal(false);
                    setCoachIdToAdd('');
                    createAlert('배정 완료', `${coachName} 강사가 추가 배정되었습니다.`, false);
                },
                onError: () => {
                    createAlert('배정 실패', '강사 추가 배정에 실패했습니다.', true);
                },
            },
        );
    };
    const bodyPhotoCoachNameById = useMemo(
        () =>
            resolvedRelations.reduce((map: Record<string, string>, relation: any) => {
                if (relation.coachId) {
                    map[relation.coachId] = relation.coachName || relation.alias || '이름 없는 강사';
                }
                return map;
            }, {}),
        [resolvedRelations],
    );

    const buildScheduleDetailObject = (row: any) => {
        const scheduleId = resolveHistoryScheduleId(row);
        const membershipName = row?.membershipName || row?.ticketName || row?.title || row?.membership?.name || '-';
        const coachId =
            resolveHistoryCoachId(row) ||
            resolvedRelations.find((rel: any) => rel.coachName && rel.coachName === row?.coachName)?.coachId ||
            primaryRelation?.coachId;

        return {
            ...row,
            _id: scheduleId,
            id: row?.id || scheduleId,
            scheduleId,
            type: row?.type || 'schedule',
            memberId: row?.memberId || memberId,
            coachId,
            alias: row?.alias || row?.memberName || member?.name || '-',
            attendanceStatus: row?.attendanceStatus || row?.status || 'ATTENDANCE',
            membershipName,
            membership:
                row?.membership ||
                (membershipName
                    ? {
                          name: membershipName,
                          status: 'active',
                      }
                    : undefined),
            isDeleted: row?.isDeleted ?? false,
            isCanceled: row?.isCanceled ?? false,
            minSession: row?.minSession ?? row?.membershipCount ?? 1,
        };
    };

    const recoverScheduleDetailObject = async (row: any) => {
        const initialDetail = buildScheduleDetailObject(row);
        if (!centerId || !row?.startTime) {
            return initialDetail;
        }

        const res = await getDailyScheduleAPI(centerId, dayjs(row.startTime).format('YYYY-MM-DD'), authorization);
        const matchedSchedule = res?.status === 200 ? findMatchingPtSchedule(res.data?.dailySchedules, row, memberId) : null;
        return matchedSchedule ? buildScheduleDetailObject({ ...row, ...matchedSchedule }) : initialDetail;
    };

    const recoverGroupSessionId = async (row: any) => {
        const explicitSessionId = resolveExplicitGroupSessionId(row);
        if (explicitSessionId || !centerId || !row?.startTime) return explicitSessionId;

        const targetDate = dayjs(row.startTime);
        const res = await getGroupSessions(centerId, {
            startDate: targetDate.startOf('day').toISOString(),
            endDate: targetDate.endOf('day').toISOString(),
        });
        const matchedSession = res?.status === 200 ? findMatchingGroupSession(res.data?.results ?? [], row) : undefined;
        return matchedSession?._id || matchedSession?.id || resolveGroupSessionId(row);
    };

    const subscriptions = (memberDetail?.subscriptions as IFacilitySubscription[]) || [];
    const checkins = memberDetail?.recentCheckins ?? [];

    const ptQueries = resolvedRelations.map((rel: any) => ({
        fetchFn: fetchAllMembershipForMember,
        params: {
            coachId: rel.coachId,
            memberId: memberId,
            accessToken: authObject?.accessToken,
        },
        enabled: !!rel.coachId && !!memberId,
        suspense: false,
    }));
    
    const { refinedResults: ptMembershipsResults } = useMultipleQueries(ptQueries);

    const activePtCount = ptMembershipsResults.reduce((acc: number, result: any) => {
        const memberships: MemberMembershipDataType[] = Array.isArray(result.data) ? result.data : [];
        const activeCount = memberships.filter((m: any) => m.status === 'active' || m.status === 'ACTIVE' || m.status === 'IN_USE').length;
        return acc + activeCount;
    }, 0);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const startEditing = () => {
        if (!member) return;
        setEditName(member.name);
        setEditPhone(formatKoreanPhoneNumberInput(member.phoneNumber));
        setEditGender(member.gender || '');
        setEditBirthDate(member.birthday ? dayjs(member.birthday).format('YYYY-MM-DD') : '');
        setEditMemo(member.memo || '');
        setEditAddress(profileAddress);
        setIsEditModalOpen(true);
    };

    const handleUpdate = async () => {
        if (!memberId) return;
        try {
            await updateMutation.mutateAsync({
                centerId,
                memberId,
                body: {
                    name: editName.trim(),
                    phoneNumber: editPhone.trim(),
                    gender: editGender || undefined,
                    birthday: editBirthDate || undefined,
                    memo: editMemo.trim(),
                    // 구서버 호환: 주소를 바꿨을 때만 싣는다(같으면 키 자체를 생략).
                    ...(isAddressSupported && editAddress.trim() !== profileAddress
                        ? { address: editAddress.trim() }
                        : {}),
                },
            });
            setIsEditModalOpen(false);
            createAlert('수정 완료', '회원 정보가 수정되었습니다.', false);
        } catch {
            createAlert('수정 실패', '회원 정보 수정에 실패했습니다.', true);
        }
    };

    const openMemoModal = () => {
        if (!member) return;
        setEditMemo(member.memo || '');
        setIsMemoModalOpen(true);
    };

    const handleMemoUpdate = async () => {
        if (!memberId || !member) return;
        try {
            await updateMutation.mutateAsync({
                centerId,
                memberId,
                body: {
                    name: member.name,
                    phoneNumber: member.phoneNumber,
                    gender: member.gender || undefined,
                    birthday: member.birthday ? dayjs(member.birthday).format('YYYY-MM-DD') : undefined,
                    memo: editMemo.trim(),
                },
            });
            setIsMemoModalOpen(false);
            createAlert('메모 수정 완료', '메모가 저장되었습니다.', false);
        } catch {
            createAlert('메모 수정 실패', '메모 저장에 실패했습니다.', true);
        }
    };

    const handleDelete = async () => {
        if (!memberId) return;
        try {
            await deleteMutation.mutateAsync({ centerId, memberId });
            createAlert('삭제 완료', '회원이 삭제되었습니다.', false);
            navigate('/memberList');
        } catch {
            createAlert('삭제 실패', '회원 삭제에 실패했습니다.', true);
        }
    };

    const subscriptionColDefs: GridColProps[] = [
        { field: 'productName', headerName: '상품명', width: 160, align: 'center' },
        { field: 'category', headerName: '카테고리', width: 90, align: 'center' },
        {
            field: 'startDate',
            headerName: '시작일',
            type: 'date',
            dateFormat: 'YYYY-MM-DD',
            width: 110,
            align: 'center',
        },
        {
            field: 'endDate',
            headerName: '종료일',
            type: 'date',
            dateFormat: 'YYYY-MM-DD',
            width: 110,
            align: 'center',
        },
        {
            field: 'price',
            headerName: '금액',
            width: 110,
            align: 'center',
            customFormat: (value) => `${Number(value).toLocaleString()}원`,
        },
        {
            field: 'status',
            headerName: '상태',
            width: 70,
            align: 'center',
            customComponent: (value) => {
                const cfg: Record<string, { label: string; bg: string; color: string }> = {
                    active: { label: '이용중', bg: '#D1FAE5', color: '#065F46' },
                    expired: { label: '만료', bg: '#F3F4F6', color: '#6B7280' },
                    refunded: { label: '환불', bg: '#FEE2E2', color: '#DC2626' },
                };
                const c = cfg[value] || { label: value, bg: '#F3F4F6', color: '#6B7280' };
                return (
                    <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 14, backgroundColor: c.bg, color: c.color }}>
                        {c.label}
                    </span>
                );
            },
        },
        {
            field: '_id',
            headerName: '',
            width: 100,
            align: 'center',
            customComponent: (value, row) => (
                row.status === 'active' ? (
                    <Row style={{ gap: 8 }}>
                        <button
                            style={{ fontSize: 14, color: '#3B82F6', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 600 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditTarget(row as IFacilitySubscription);
                            }}
                        >
                            수정
                        </button>
                        <button
                            style={{ fontSize: 14, color: '#EF4444', cursor: 'pointer', background: 'none', border: 'none' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setRefundTarget(row as IFacilitySubscription);
                            }}
                        >
                            환불
                        </button>
                    </Row>
                ) : <></>
            ),
        },
    ];

    const checkinColDefs: GridColProps[] = [
        {
            field: 'checkinAt',
            headerName: '체크인 시간',
            type: 'date',
            dateFormat: 'YYYY-MM-DD HH:mm',
            width: 180,
            align: 'center',
        },
        {
            field: 'checkoutAt',
            headerName: '체크아웃 시간',
            type: 'date',
            dateFormat: 'YYYY-MM-DD HH:mm',
            width: 180,
            align: 'center',
            nullValue: '-',
        },
        {
            field: 'method',
            headerName: '방법',
            width: 100,
            align: 'center',
            customFormat: (value) => {
                const labels: Record<string, string> = { kiosk_phone: '키오스크', qr: 'QR', manual: '수동', admin_call: '관리자호출' };
                return labels[value] || value;
            },
        },
    ];

    // 활성 이용권 수
    const activeSubCount = subscriptions.filter((s: any) => s.status === 'active').length;

    const tabs: Array<MemberDetailTabItem & { key: Tab }> = [
        { key: 'history', label: '수업 기록', icon: '🗓️' },
        ...(canViewBodyPhotos
            ? [
                  {
                      key: 'bodyPhoto' as Tab,
                      label: '눈바디',
                      icon: '📷',
                      count: bodyPhotoQuery.isFetched ? bodyPhotoQuery.data?.length ?? 0 : undefined,
                  },
              ]
            : []),
        ...(hasMemberNewOpportunities
            ? [{ key: 'newOpportunity' as Tab, label: '신규기회', icon: '◎', count: memberNewOpportunities.length }]
            : []),
        {
            key: 'pt',
            label: '수강권',
            icon: '💪',
            count: activePtCount + sharedPtReceivedCount,
        },
        { key: 'subscription', label: '이용권', icon: '🎫', count: activeSubCount },
        { key: 'group', label: '그룹 수업', icon: '🏋️', count: activeGroupTicketCount },
        { key: 'checkin', label: '출석 기록', icon: '📋', count: checkins.length },
    ];

    const hasActivePt = activePtCount > 0 || !!(memberDetail as any)?.ptInfo?.hasActivePT;
    const hasActiveGroupTicket = activeGroupTicketCount > 0;
    const fallbackStatus = (ptInfo?.memberships?.length ?? 0) > 0
        ? 'expired'
        : (resolvedRelations.length > 0 ? 'empty' : 'none');
    const serverStatus = (memberDetail?.centerMember as any)?.ptStatus;
    const resolvedStatus = hasActivePt || hasActiveGroupTicket
        ? 'active'
        : (serverStatus || fallbackStatus);
    const statusCfg = STATUS_CONFIG[resolvedStatus] || STATUS_CONFIG.active;

    // 가입일 Fallback 처리
    const joinedDateStr = (memberDetail?.centerMember as any)?.joinedAt || (memberDetail?.member as any)?.createdAt || (memberDetail?.centerMember as any)?.createdAt;

    // 가입 경과일
    const daysSinceJoin = joinedDateStr
        ? dayjs().diff(dayjs(joinedDateStr), 'day')
        : 0;

    const inputStyle: React.CSSProperties = {
        border: '1px solid #E1E4E6',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 14,
        width: '100%',
        outline: 'none',
    };
    const actionButtonBaseStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 42,
        padding: '10px 16px',
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1.2,
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        whiteSpace: 'nowrap',
    };
    const secondaryActionButtonStyle: React.CSSProperties = {
        ...actionButtonBaseStyle,
        backgroundColor: 'white',
        color: '#374151',
        border: '1px solid #D1D5DB',
    };
    const primaryActionButtonStyle: React.CSSProperties = {
        ...actionButtonBaseStyle,
        backgroundColor: '#2563EB',
        color: 'white',
        border: '1px solid #2563EB',
    };
    const accentActionButtonStyle: React.CSSProperties = {
        ...actionButtonBaseStyle,
        background: 'linear-gradient(135deg, #7C3AED, #6D28D9)',
        color: 'white',
        border: 'none',
    };
    const dangerActionButtonStyle: React.CSSProperties = {
        ...actionButtonBaseStyle,
        backgroundColor: 'white',
        color: '#DC2626',
        border: '1px solid #FCA5A5',
    };
    const profileStats = [
        {
            label: '가입일',
            value: joinedDateStr ? dayjs(joinedDateStr).format('YY.MM.DD') : '-',
            sub: `${daysSinceJoin}일 경과`,
        },
        {
            label: '수강권',
            // 공유받은 수강권도 사용 가능한 권리이므로 함께 센다 (아래 목록·탭과 일치).
            value: `${(ptInfo?.memberships?.length || 0) + sharedPtReceivedCount}건`,
            sub: hasActivePt ? 'PT 진행중' : '진행중인 PT 없음',
        },
        {
            label: '이용권',
            value: `${activeSubCount}건`,
            sub: `총 ${subscriptions.length}건`,
        },
        {
            label: '출석',
            value: `${checkins.length}회`,
            sub: checkins.length > 0 ? `최근 ${dayjs((checkins[0] as any)?.checkinAt).format('MM/DD')}` : '출석 기록 없음',
        },
    ];
    const renderPanelEmptyState = (icon: string, title: string, description: string) => (
        <Col
            style={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 320,
                padding: '64px 24px',
                borderRadius: 18,
                border: '1px dashed #D1D5DB',
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                textAlign: 'center',
            }}
        >
            <div style={{ fontSize: 44, marginBottom: 16 }}>{icon}</div>
            <div
                style={{
                    fontSize: 19,
                    fontWeight: 700,
                    color: '#111827',
                    marginBottom: 8,
                    letterSpacing: -0.3,
                }}
            >
                {title}
            </div>
            <div
                style={{
                    maxWidth: 420,
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: '#6B7280',
                }}
            >
                {description}
            </div>
        </Col>
    );

    if (loadingDetail) {
        return (
            <Col
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#F8FAFC',
                    marginTop: 64,
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                }}
            >
                <div style={{ width: 36, height: 36, border: '4px solid #3B82F6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <CaptionLight color={colors.LAVEL_1}>회원 정보를 불러오는 중...</CaptionLight>
            </Col>
        );
    }

    /**
     * 회원 조회 실패 화면.
     * 로딩과 같은 층위에서 early return 하므로, 이 아래의 등록/수정 진입점
     * ('+ 이용권 등록', 수강권 발급, 각종 모달)은 전부 렌더되지 않는다.
     */
    if (isMemberDetailUnavailable) {
        return (
            <Col
                style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#F8FAFC',
                    marginTop: 64,
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 24px',
                }}
            >
                <Col
                    style={{
                        alignItems: 'center',
                        textAlign: 'center',
                        maxWidth: 520,
                        padding: '48px 32px',
                        borderRadius: 18,
                        border: '1px solid #FECACA',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                    }}
                >
                    <div style={{ fontSize: 44, marginBottom: 16 }}>⚠️</div>
                    <div
                        style={{
                            fontSize: 19,
                            fontWeight: 700,
                            color: '#111827',
                            marginBottom: 8,
                            letterSpacing: -0.3,
                        }}
                    >
                        회원 정보를 불러올 수 없습니다
                    </div>
                    <div style={{ fontSize: 15, lineHeight: 1.6, color: '#6B7280', whiteSpace: 'pre-line' }}>
                        {isMemberNotFound
                            ? '이 회원 정보는 삭제되었거나 이 센터에 없습니다.\n회원 목록에서 다시 확인해주세요.'
                            : '일시적인 오류로 회원 정보를 가져오지 못했습니다.\n잠시 후 다시 시도해주세요.'}
                    </div>
                    <Row style={{ gap: 8, marginTop: 24 }}>
                        <button onClick={() => navigate('/memberList')} style={primaryActionButtonStyle}>
                            회원 목록으로
                        </button>
                        {!isMemberNotFound && (
                            <button onClick={() => void refetch()} style={secondaryActionButtonStyle}>
                                다시 시도
                            </button>
                        )}
                    </Row>
                </Col>
            </Col>
        );
    }

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F4F7FB',
                marginTop: 64,
                overflow: 'auto',
            }}
        >
            <Col style={{ width: '100%', maxWidth: 1920, margin: '0 auto', padding: '32px 48px 72px' }}>
                <button
                    onClick={() => navigate('/memberList')}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#6B7280',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 0',
                        marginBottom: 24,
                        transition: 'color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#111827'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#6B7280'; }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    회원 목록
                </button>

                {isReadOnly && (
                    <div style={{ marginBottom: 20 }}>
                        <WithdrawnMemberReadOnlyNotice />
                    </div>
                )}

                {/* ═══════ 프로필 헤더 카드 ═══════ */}
                <div style={{
                    background: 'white',
                    borderRadius: 20,
                    padding: '34px 40px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.05)',
                    marginBottom: 20,
                }}>
                    {/* 상단: 아바타 + 이름 + 버튼 */}
                    <Row className="items-start justify-between" style={{ gap: 24 }}>
                        <Row className="items-center gap-[24px]" style={{ flex: 1, minWidth: 0 }}>
                            {/* 아바타 */}
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                                <div style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    background: member?.profileImageUrl ? 'none' : 'linear-gradient(135deg, #6366F1, #3B82F6)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 28,
                                    fontWeight: 700,
                                    color: 'white',
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.18)',
                                }}>
                                    {member?.profileImageUrl ? (
                                        <img src={member.profileImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        member?.name?.charAt(0) || '?'
                                    )}
                                </div>
                                {!isReadOnly && <button
                                    onClick={() => profileImageInputRef.current?.click()}
                                    style={{
                                        position: 'absolute', bottom: -2, right: -2,
                                        width: 28, height: 28, borderRadius: '50%',
                                        fontSize: 13, backgroundColor: 'white',
                                        border: '1.5px solid #E5E7EB', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                    }}
                                    title="사진 수정"
                                >📷</button>}
                                {!isReadOnly && <input ref={profileImageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileImageUpload} />}
                            </div>

                            {/* 이름 + 상태 + 기본정보 */}
                            <Col style={{ gap: 10, minWidth: 0 }}>
                                <Row className="items-center gap-[10px]" style={{ flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 32, fontWeight: 800, color: '#111827', letterSpacing: -0.7, lineHeight: 1.1 }}>
                                        {member?.name || '회원 상세'}
                                    </span>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                                        backgroundColor: statusCfg.bg, color: statusCfg.color,
                                    }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusCfg.dot }} />
                                        {statusCfg.label}
                                    </span>
                                    {resolvedRelations.length > 0 && (
                                        resolvedRelations.map((rel: any, i: number) => (
                                            <span key={i} style={{
                                                padding: '5px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                                                backgroundColor: rel.isPrimary ? '#DBEAFE' : '#EFF6FF',
                                                color: '#2563EB', border: '1px solid #BFDBFE',
                                            }}>
                                                {rel.coachName || rel.alias || rel.coachId}
                                                {rel.isPrimary ? ' · 대표' : ''}
                                            </span>
                                        ))
                                    )}
                                    {!isReadOnly && (
                                        <button
                                            type="button"
                                            onClick={() => setIsAddCoachModal(true)}
                                            disabled={availableCoachOptions.length === 0}
                                            title={availableCoachOptions.length === 0 ? '추가로 배정할 수 있는 강사가 없습니다.' : '강사 추가 배정'}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '5px 12px',
                                                borderRadius: 999,
                                                fontSize: 13,
                                                fontWeight: 700,
                                                backgroundColor: '#FFFFFF',
                                                color: availableCoachOptions.length === 0 ? '#9CA3AF' : '#2563EB',
                                                border: `1px dashed ${availableCoachOptions.length === 0 ? '#D1D5DB' : '#93C5FD'}`,
                                                cursor: availableCoachOptions.length === 0 ? 'not-allowed' : 'pointer',
                                                opacity: availableCoachOptions.length === 0 ? 0.65 : 1,
                                            }}
                                        >
                                            + 강사 추가
                                        </button>
                                    )}
                                </Row>
                                <Row className="items-center gap-[12px]" style={{ fontSize: 15, color: '#6B7280', flexWrap: 'wrap', lineHeight: 1.6 }}>
                                    <span>{formatKoreanPhoneNumber(member?.phoneNumber, '-')}</span>
                                    <span style={{ color: '#E5E7EB' }}>·</span>
                                    <span>{member?.gender === 'male' ? '남성' : member?.gender === 'female' ? '여성' : '성별 미등록'}</span>
                                    {member?.birthday && (
                                        <>
                                            <span style={{ color: '#E5E7EB' }}>·</span>
                                            <span>{dayjs(member.birthday).format('YYYY.MM.DD')}</span>
                                        </>
                                    )}
                                    {profileHeight !== null && (
                                        <>
                                            <span style={{ color: '#E5E7EB' }}>·</span>
                                            <span>키 {profileHeight}cm</span>
                                        </>
                                    )}
                                    {profileStartWeight !== null && (
                                        <>
                                            <span style={{ color: '#E5E7EB' }}>·</span>
                                            <span>시작 체중 {profileStartWeight}kg</span>
                                        </>
                                    )}
                                    {profileAddress && (
                                        <>
                                            <span style={{ color: '#E5E7EB' }}>·</span>
                                            <span>{profileAddress}</span>
                                        </>
                                    )}
                                    <span style={{ color: '#E5E7EB' }}>·</span>
                                    <AppAccountStatusBadge
                                        member={{ ...member, onboardingPending, onboardingStale }}
                                        showWithdrawnAt
                                        showOnboardingStatus
                                    />
                                </Row>
                            </Col>
                        </Row>

                        {/* 우측: 버튼들 */}
                        <Row className="gap-[8px] items-center" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {!isReadOnly && (member?.memberType === 'temp' || member?.memberType === 'public') && (
                                <button onClick={() => setIsMergeModalOpen(true)} style={accentActionButtonStyle}>회원 병합</button>
                            )}
                            <button onClick={openMemoModal} style={secondaryActionButtonStyle}>{isReadOnly ? '메모 보기' : '메모'}</button>
                            {primaryRelation && (
                                <button
                                    onClick={() => navigate('/feed/member', { state: { coachId: primaryRelation.coachId, memberId } })}
                                    style={secondaryActionButtonStyle}
                                >
                                    수업 피드
                                </button>
                            )}
                            {!isReadOnly && <button
                                onClick={() => setIsTicketTransferModal(true)}
                                style={{ ...secondaryActionButtonStyle, position: 'relative' }}
                            >
                                수강권 양도
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: -6,
                                        right: -8,
                                        padding: '2px 6px',
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        backgroundColor: '#EF4444',
                                        color: '#FFFFFF',
                                        lineHeight: 1,
                                        letterSpacing: 0.3,
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    }}
                                >
                                    NEW
                                </span>
                            </button>}
                            {!isReadOnly && resolvedRelations.length === 0 && (
                                <button
                                    onClick={() => {
                                        setChangeCoachFailure(null);
                                        setIsChangeCoachModal(true);
                                    }}
                                    style={primaryActionButtonStyle}
                                >
                                    강사 배정
                                </button>
                            )}
                            {!isReadOnly && resolvedRelations.length >= 1 && (
                                <button
                                    onClick={() => {
                                        setChangeCoachFailure(null);
                                        setIsChangeCoachModal(true);
                                    }}
                                    style={secondaryActionButtonStyle}
                                >
                                    강사 변경(인계)
                                </button>
                            )}
                            {!isReadOnly && <button onClick={startEditing} style={primaryActionButtonStyle}>수정</button>}
                            {(!isReadOnly || canRemovePreservedMember) && (
                                <button onClick={() => setIsDeleteVisible(true)} style={dangerActionButtonStyle}>
                                    {canRemovePreservedMember ? '센터 목록에서 삭제' : '삭제'}
                                </button>
                            )}
                        </Row>
                    </Row>

                    {/* 하단: 통계 */}
                    <Row className="gap-[16px]" style={{ marginTop: 28, flexWrap: 'wrap' }}>
                        {profileStats.map((stat) => (
                            <Col
                                key={stat.label}
                                style={{
                                    flex: '1 1 220px',
                                    minWidth: 0,
                                    padding: '20px 22px',
                                    borderRadius: 16,
                                    border: '1px solid #E5E7EB',
                                    background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        fontSize: 13,
                                        color: '#6B7280',
                                        fontWeight: 700,
                                        letterSpacing: -0.1,
                                    }}
                                >
                                    {stat.label}
                                </span>
                                <span
                                    style={{
                                        display: 'block',
                                        fontSize: 32,
                                        fontWeight: 800,
                                        color: '#111827',
                                        marginTop: 8,
                                        letterSpacing: -1,
                                        lineHeight: 1.05,
                                    }}
                                >
                                    {stat.value}
                                </span>
                                {stat.sub && (
                                    <span
                                        style={{
                                            display: 'block',
                                            fontSize: 14,
                                            color: '#9CA3AF',
                                            marginTop: 8,
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {stat.sub}
                                    </span>
                                )}
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* ═══════ 탭 네비게이션 ═══════ */}
                <div style={{
                    background: 'white',
                    borderRadius: 20,
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                }}>
                    <MemberDetailTabBar
                        tabs={tabs}
                        activeTab={activeTab}
                        onChange={(tabKey) => setActiveTab(tabKey as Tab)}
                    />

                    {/* 탭 콘텐츠 */}
                    <div
                        id={`member-detail-panel-${activeTab}`}
                        role="tabpanel"
                        aria-labelledby={`member-detail-tab-${activeTab}`}
                        style={{ padding: '34px 40px 40px', minHeight: 420 }}
                    >

                        {/* ──── 수업 기록 탭 ──── PT + 그룹 통합 (B안) */}
                        {activeTab === 'history' && member && (
                            <Col className="gap-[24px]">
                                {resolvedRelations.length ? (
                                    <MemberClassHistoryComponents
                                        coachIds={resolvedRelations.map((rel: any) => rel.coachId)}
                                        memberId={memberId}
                                        hideTitle={true}
                                        coachMap={resolvedRelations.reduce((map: Record<string, string>, rel: any) => {
                                            map[rel.coachId] = rel.coachName || rel.alias || '-';
                                            return map;
                                        }, {} as Record<string, string>)}
                                        onClickRow={async (row: any) => {
                                            if (row?.source === 'group') {
                                                if (isReadOnly) {
                                                    createAlert(
                                                        '조회 전용',
                                                        '탈퇴 회원의 그룹 수업 기록은 이 화면에서 조회만 가능합니다.',
                                                        false
                                                    );
                                                    return;
                                                }
                                                const sessionId = await recoverGroupSessionId(row);
                                                if (sessionId) {
                                                    setGroupSessionPopoverId(sessionId);
                                                } else {
                                                    createAlert('오류', '그룹 수업 정보를 찾을 수 없습니다.', true);
                                                }
                                                return;
                                            }
                                            const detail = await recoverScheduleDetailObject(row);
                                            setIsVisibleScheduleDetail(true);
                                            setDetailObject(detail);
                                        }}
                                    />
                                ) : (
                                    renderPanelEmptyState('🗓️', '수업 기록이 없습니다', '담당 강사가 아직 지정되지 않아 확인할 수 있는 수업 기록이 없습니다.')
                                )}

                                {/* 타입 전환 이력 */}
                                {memberDetail?.memberTypeHistory && memberDetail.memberTypeHistory.length > 0 && (
                                    <Col style={{ paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: '#374151', marginBottom: 16 }}>타입 전환 이력</span>
                                        <Col className="gap-[8px]">
                                            {memberDetail.memberTypeHistory.map((h: any, i: number) => (
                                                <Row
                                                    key={i}
                                                    className="items-center gap-[8px]"
                                                    style={{ paddingLeft: 12, borderLeft: '2px solid #93C5FD' }}
                                                >
                                                    <span style={{ fontSize: 15, color: '#9CA3AF' }}>
                                                        {dayjs(h.changedAt).format('YYYY-MM-DD')}
                                                    </span>
                                                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 14, fontWeight: 700, backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                                                        {getMemberTypeHistoryLabel(h.from)}
                                                    </span>
                                                    <span style={{ fontSize: 15, color: '#D1D5DB' }}>→</span>
                                                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 14, fontWeight: 700, backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                                                        {getMemberTypeHistoryLabel(h.to)}
                                                    </span>
                                                    {h.reason && (
                                                        <span style={{ fontSize: 15, color: '#9CA3AF' }}>
                                                            ({getMemberTypeChangeReasonLabel(h.reason)})
                                                        </span>
                                                    )}
                                                </Row>
                                            ))}
                                        </Col>
                                    </Col>
                                )}
                            </Col>
                        )}

                        {/* ──── 눈바디 탭 ──── 회원이 강사에게 공유한 기록만 조회 */}
                        {activeTab === 'bodyPhoto' && canViewBodyPhotos && (
                            <MemberBodyPhotoTab
                                photos={bodyPhotoQuery.data ?? []}
                                isLoading={bodyPhotoQuery.isLoading}
                                isRefreshing={bodyPhotoQuery.isFetching}
                                isError={bodyPhotoQuery.isError}
                                onRetry={() => {
                                    void bodyPhotoQuery.refetch();
                                }}
                                coachNameById={bodyPhotoCoachNameById}
                            />
                        )}

                        {/* ──── 신규기회 탭 ──── 수업 기록과 분리 */}
                        {activeTab === 'newOpportunity' && (
                            <NewOpportunityProfileHistory
                                opportunities={memberNewOpportunities}
                                isLoading={newOpportunityQuery.isLoading}
                                renderEmpty={renderPanelEmptyState}
                            />
                        )}

                        {/* ──── 이용권 탭 ──── */}
                        {activeTab === 'subscription' && (() => {
                            const CATEGORY_LABEL: Record<string, string> = { gym: '헬스장', locker: '락커', clothing: '운동복', etc: '기타' };
                            const ICONS: Record<string, string> = { gym: '🏋️', locker: '🔐', clothing: '👕', etc: '📦' };
                            const STATUS_CFG: Record<string, { label: string; bg: string; color: string; border: string }> = {
                                active: { label: '이용중', bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' },
                                expired: { label: '만료', bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
                                refunded: { label: '환불', bg: '#FEE2E2', color: '#DC2626', border: '#FECACA' },
                            };
                            const PAY_METHOD_LABEL: Record<string, string> = { card: '💳 카드', cash: '💵 현금', transfer: '🏦 계좌이체' };
                            const activeSubs = subscriptions.filter((s: any) => s.status !== 'refunded');
                            const refundedSubs = subscriptions.filter((s: any) => s.status === 'refunded');

                            const renderCard = (sub: any) => {
                                const s = STATUS_CFG[sub.status] || STATUS_CFG.expired;
                                const dDay = dayjs(sub.endDate).diff(dayjs(), 'day');
                                const isUrgent = sub.status === 'active' && dDay <= 7;
                                // 회수된 락커는 displayLockerNumber에 이전 번호가 남으므로
                                // 현재 배정 여부(isLockerCurrentlyAssigned)와 원본 lockerNumber로 판단
                                const lockerAssigned = sub.isLockerCurrentlyAssigned ?? Boolean(sub.lockerNumber);

                                            return (
                                                <div
                                                    key={sub._id}
                                                    onClick={() => setSelectedSubscription(sub)}
                                                    style={{
                                                        padding: '16px 20px',
                                                        borderRadius: 12,
                                                        border: `1px solid ${isUrgent ? '#FDE68A' : s.border}`,
                                                        backgroundColor: isUrgent ? '#FFFBEB' : '#FAFAFA',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F0F4FF'; e.currentTarget.style.borderColor = '#93C5FD'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isUrgent ? '#FFFBEB' : '#FAFAFA'; e.currentTarget.style.borderColor = isUrgent ? '#FDE68A' : s.border; }}
                                                >
                                                    <Row className="justify-between items-start">
                                                        {/* 왼쪽: 아이콘 + 상품 정보 */}
                                                        <Row style={{ gap: 14, alignItems: 'flex-start' }}>
                                                            <div style={{
                                                                width: 40, height: 40, borderRadius: 10,
                                                                backgroundColor: '#EFF6FF', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 20, flexShrink: 0,
                                                            }}>
                                                                {ICONS[sub.category] || '📦'}
                                                            </div>
                                                            <Col style={{ gap: 4 }}>
                                                                <Row style={{ gap: 8, alignItems: 'center' }}>
                                                                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                                                                        {sub.productName}
                                                                    </span>
                                                                    {/* 전부 넘긴 이용권은 「만료」 대신 「넘긴 이용권」 하나만 보인다 */}
                                                                    {sub.transferKind !== 'CLOSED_SOURCE' && (
                                                                        <span style={{
                                                                            padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                            fontWeight: 600, backgroundColor: s.bg, color: s.color,
                                                                        }}>
                                                                            {s.label}
                                                                        </span>
                                                                    )}
                                                                    <TransferKindBadge kind={sub.transferKind} />
                                                                    <span style={{
                                                                        padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                        backgroundColor: '#F3F4F6', color: '#6B7280',
                                                                    }}>
                                                                        {CATEGORY_LABEL[sub.category] || sub.category}
                                                                    </span>
                                                                    {sub.category === 'locker' && lockerAssigned && sub.lockerNumber && (
                                                                        <span style={{
                                                                            padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                            fontWeight: 700, backgroundColor: '#FEF3C7',
                                                                            color: '#92400E', border: '1px solid #FDE68A',
                                                                        }}>
                                                                            🔐 {sub.lockerNumber}번
                                                                        </span>
                                                                    )}
                                                                    {sub.paymentMethod && (
                                                                        <span style={{
                                                                            padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                            backgroundColor: '#F3F4F6', color: '#374151',
                                                                        }}>
                                                                            {PAY_METHOD_LABEL[sub.paymentMethod] || sub.paymentMethod}
                                                                        </span>
                                                                    )}
                                                                </Row>
                                                                <Row style={{ gap: 12, fontSize: 15, color: '#6B7280', flexWrap: 'wrap' }}>
                                                                    <span>📅 {dayjs(sub.startDate).format('YY.MM.DD')} ~ {dayjs(sub.endDate).format('YY.MM.DD')}</span>
                                                                    {sub.status === 'active' && (
                                                                        <span style={{ color: isUrgent ? '#DC2626' : '#2563EB', fontWeight: 600 }}>
                                                                            D-{dDay > 0 ? dDay : 0}
                                                                        </span>
                                                                    )}
                                                                    <span>💰 {Number(sub.price).toLocaleString()}원</span>
                                                                </Row>
                                                            </Col>
                                                        </Row>

                                                        {/* 오른쪽: 액션 버튼 */}
                                                        {!isReadOnly && <Row style={{ gap: 6, flexShrink: 0 }}>
                                                            {sub.status === 'active' && (
                                                                <>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); setEditTarget(sub); }}
                                                                        style={{
                                                                            padding: '4px 10px', borderRadius: 6, fontSize: 14,
                                                                            fontWeight: 600, color: '#3B82F6', backgroundColor: '#EFF6FF',
                                                                            border: '1px solid #BFDBFE', cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        수정
                                                                    </button>
                                                                    {!isCenterViewer && <button
                                                                        onClick={(e) => { e.stopPropagation(); setTransferTarget(sub); }}
                                                                        style={{
                                                                            padding: '4px 10px', borderRadius: 6, fontSize: 14,
                                                                            fontWeight: 600, color: '#4338CA', backgroundColor: '#EEF2FF',
                                                                            border: '1px solid #C7D2FE', cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        {hasPendingFacilityTransfer(sub._id) ? '이어서 양도하기' : '양도'}
                                                                    </button>}
                                                                    {getFacilityTransferLocks(sub).canRefund && !isCenterViewer && (
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); setRefundTarget(sub); }}
                                                                            style={{
                                                                                padding: '4px 10px', borderRadius: 6, fontSize: 14,
                                                                                fontWeight: 600, color: '#F59E0B', backgroundColor: '#FFFBEB',
                                                                                border: '1px solid #FDE68A', cursor: 'pointer',
                                                                            }}
                                                                        >
                                                                            환불
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                            {/* 결과를 모르는 양도가 남아 있으면 이용 중이 아니어도(전부 양도 중 원본이 만료됨) 마무리 진입을 보인다 */}
                                                            {sub.status !== 'active' && !isCenterViewer && hasPendingFacilityTransfer(sub._id) && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setTransferTarget(sub); }}
                                                                    style={{
                                                                        padding: '4px 10px', borderRadius: 6, fontSize: 14,
                                                                        fontWeight: 600, color: '#B45309', backgroundColor: '#FFFBEB',
                                                                        border: '1px solid #FCD34D', cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    이어서 양도하기
                                                                </button>
                                                            )}
                                                            {/* 양도 표식이 있는 이용권은 서버가 삭제를 막는다(매출 소멸 방지) */}
                                                            {getFacilityTransferLocks(sub).canDelete && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setDeleteSubTarget({ sub, isRefunded: false });
                                                                    }}
                                                                    style={{
                                                                        padding: '4px 10px', borderRadius: 6, fontSize: 14,
                                                                        fontWeight: 600, color: '#EF4444', backgroundColor: '#FEF2F2',
                                                                        border: '1px solid #FECACA', cursor: 'pointer',
                                                                    }}
                                                                >
                                                                    삭제
                                                                </button>
                                                            )}
                                                        </Row>}
                                                    </Row>
                                                </div>
                                            );
                                };

                            return (
                            <Col>
                                <Row className="justify-between items-center" style={{ marginBottom: 10 }}>
                                    <span style={{ fontSize: 18, fontWeight: 700, color: '#374151' }}>이용권 {subscriptions.length}건</span>
                                    <Row style={{ gap: 8, alignItems: 'center' }}>
                                        {/* 양도 표식이 있는 이용권이 있을 때만(구서버에는 표식이 없어 이력 경로도 부르지 않는다) */}
                                        {subscriptions.some((s: any) => !!s.transferKind) && (
                                            <button
                                                type="button"
                                                onClick={() => setIsTransferHistoryVisible(true)}
                                                style={{
                                                    padding: '6px 12px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                                                    color: '#4338CA', backgroundColor: 'white', border: '1px solid #C7D2FE', cursor: 'pointer',
                                                }}
                                            >
                                                양도 이력
                                            </button>
                                        )}
                                        {!isReadOnly && (
                                            <ColorSquareButton
                                                text="+ 이용권 등록"
                                                size="small"
                                                callBack={() => setIsSubscriptionModalVisible(true)}
                                            />
                                        )}
                                    </Row>
                                </Row>
                                <span style={{ fontSize: 14, color: '#7C3AED', marginBottom: 18 }}>
                                    그룹수업(GX) 수강권은 <b>그룹</b> 탭에서 발급해주세요
                                </span>

                                {activeSubs.length > 0 ? (
                                    <Col style={{ gap: 12 }}>
                                        {activeSubs.map(renderCard)}
                                    </Col>
                                ) : (
                                    renderPanelEmptyState('🎫', '등록된 이용권이 없습니다', '헬스장, 락커, 운동복 같은 시설 이용권을 등록하면 이곳에서 한눈에 관리할 수 있습니다.')
                                )}

                                {/* 환불 내역 */}
                                {refundedSubs.length > 0 && (
                                    <Col style={{ marginTop: 24 }}>
                                        <span style={{ fontSize: 15, fontWeight: 600, color: '#9CA3AF', marginBottom: 10 }}>
                                            환불 내역 ({refundedSubs.length}건)
                                        </span>
                                        <Col style={{ gap: 8 }}>
                                            {refundedSubs.map((sub: any) => {
                                                return (
                                                    <div
                                                        key={sub._id}
                                                        onClick={() => setSelectedSubscription(sub)}
                                                        style={{
                                                            padding: '12px 16px', borderRadius: 10,
                                                            border: '1px solid #FECACA', backgroundColor: '#FEF2F2',
                                                            cursor: 'pointer', opacity: 0.85,
                                                        }}
                                                    >
                                                        <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Row style={{ gap: 10, alignItems: 'center' }}>
                                                                <span style={{ fontSize: 16 }}>{ICONS[sub.category] || '📦'}</span>
                                                                <span style={{ fontSize: 15, fontWeight: 600, color: '#6B7280', textDecoration: 'line-through' }}>
                                                                    {sub.productName}
                                                                </span>
                                                                <span style={{
                                                                    padding: '1px 6px', borderRadius: 4, fontSize: 15,
                                                                    fontWeight: 600, backgroundColor: '#FEE2E2', color: '#DC2626',
                                                                }}>환불</span>
                                                                <TransferKindBadge kind={sub.transferKind} />
                                                            </Row>
                                                            <Row style={{ gap: 8, alignItems: 'center' }}>
                                                                <span style={{ fontSize: 14, color: '#9CA3AF' }}>
                                                                    {Number(sub.price).toLocaleString()}원
                                                                </span>
                                                                {!isReadOnly && getFacilityTransferLocks(sub).canDelete && <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setDeleteSubTarget({ sub, isRefunded: true });
                                                                    }}
                                                                    style={{
                                                                        padding: '2px 8px', borderRadius: 4, fontSize: 14,
                                                                        fontWeight: 600, color: '#EF4444', backgroundColor: '#FEF2F2',
                                                                        border: '1px solid #FECACA', cursor: 'pointer',
                                                                    }}
                                                                >삭제</button>}
                                                            </Row>
                                                        </Row>
                                                    </div>
                                                );
                                            })}
                                        </Col>
                                    </Col>
                                )}
                            </Col>
                            );
                        })()}

                        {/* ──── 이용권 상세 팝업 ──── */}
                        {selectedSubscription && (
                            <div
                                style={{
                                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                                }}
                                onClick={() => setSelectedSubscription(null)}
                            >
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        backgroundColor: 'white', borderRadius: 16, padding: '28px 32px',
                                        width: 440, maxHeight: '80vh', overflowY: 'auto',
                                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                    }}
                                >
                                    <Row className="justify-between items-center" style={{ marginBottom: 20 }}>
                                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>이용권 상세</span>
                                        <button
                                            onClick={() => setSelectedSubscription(null)}
                                            style={{ fontSize: 20, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}
                                        >✕</button>
                                    </Row>

                                    {(() => {
                                        const sub = selectedSubscription;
                                        const catLabel: Record<string, string> = { gym: '헬스장', locker: '락커', clothing: '운동복', etc: '기타' };
                                        const iconsMap: Record<string, string> = { gym: '🏋️', locker: '🔐', clothing: '👕', etc: '📦' };
                                        const statusCfg: Record<string, { label: string; bg: string; color: string }> = {
                                            active: { label: '이용중', bg: '#D1FAE5', color: '#065F46' },
                                            expired: { label: '만료', bg: '#F3F4F6', color: '#6B7280' },
                                            refunded: { label: '환불', bg: '#FEE2E2', color: '#DC2626' },
                                        };
                                        const payMethodLabel: Record<string, string> = { card: '💳 카드', cash: '💵 현금', transfer: '🏦 계좌이체' };
                                        const s = statusCfg[sub.status] || statusCfg.expired;
                                        const dDay = dayjs(sub.endDate).diff(dayjs(), 'day');
                                        const lockerAssigned = sub.isLockerCurrentlyAssigned ?? Boolean(sub.lockerNumber);

                                        const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
                                            <Row style={{ justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}>
                                                <span style={{ fontSize: 15, color: '#6B7280' }}>{label}</span>
                                                <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{value}</span>
                                            </Row>
                                        );

                                        return (
                                            <Col>
                                                {/* 상단 카드 */}
                                                <div style={{
                                                    padding: '16px', borderRadius: 12,
                                                    backgroundColor: '#F8FAFC', marginBottom: 20,
                                                    display: 'flex', alignItems: 'center', gap: 14,
                                                }}>
                                                    <div style={{
                                                        width: 48, height: 48, borderRadius: 12,
                                                        backgroundColor: '#EFF6FF', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', fontSize: 24,
                                                    }}>
                                                        {iconsMap[sub.category] || '📦'}
                                                    </div>
                                                    <Col style={{ gap: 4 }}>
                                                        <span style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{sub.productName}</span>
                                                        <Row style={{ gap: 6 }}>
                                                            <span style={{
                                                                padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                fontWeight: 600, backgroundColor: s.bg, color: s.color,
                                                            }}>{s.label}</span>
                                                            <TransferKindBadge kind={sub.transferKind} />
                                                            {sub.status === 'active' && (
                                                                <span style={{
                                                                    padding: '2px 8px', borderRadius: 6, fontSize: 14,
                                                                    fontWeight: 600, color: dDay <= 7 ? '#DC2626' : '#2563EB',
                                                                    backgroundColor: dDay <= 7 ? '#FEF2F2' : '#EFF6FF',
                                                                }}>D-{dDay > 0 ? dDay : 0}</span>
                                                            )}
                                                        </Row>
                                                    </Col>
                                                </div>

                                                {/* 상세 정보 */}
                                                <InfoRow label="카테고리" value={catLabel[sub.category] || sub.category} />
                                                {sub.category === 'locker' && (
                                                    <InfoRow label="락커 번호" value={
                                                        lockerAssigned && sub.lockerNumber
                                                            ? `🔐 ${sub.lockerNumber}번`
                                                            : sub.releasedLockerNumber
                                                              ? <span style={{ color: '#9CA3AF' }}>회수됨 (이전 {sub.releasedLockerNumber}번)</span>
                                                              : <span style={{ color: '#9CA3AF' }}>미배정</span>
                                                    } />
                                                )}
                                                <InfoRow label="시작일" value={dayjs(sub.startDate).format('YYYY-MM-DD')} />
                                                <InfoRow label="종료일" value={dayjs(sub.endDate).format('YYYY-MM-DD')} />
                                                <InfoRow label="금액" value={`${Number(sub.price).toLocaleString()}원`} />
                                                <InfoRow label="결제 수단" value={
                                                    sub.paymentMethod
                                                        ? payMethodLabel[sub.paymentMethod] || sub.paymentMethod
                                                        : <span style={{ color: '#9CA3AF' }}>미입력</span>
                                                } />
                                                {sub.paymentDate && (
                                                    <InfoRow label="결제일" value={dayjs(sub.paymentDate).format('YYYY-MM-DD')} />
                                                )}

                                                {sub.memo && (
                                                    <InfoRow label="메모" value={sub.memo} />
                                                )}
                                                <InfoRow label="등록일시" value={dayjs(sub.createdAt).format('YYYY-MM-DD HH:mm')} />

                                                {sub.transferKind && (
                                                    <Col style={{ gap: 8, marginTop: 18 }}>
                                                        <span style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>양도 기록</span>
                                                        <SubscriptionTransferHistoryList
                                                            centerId={centerId}
                                                            subscriptionId={sub._id}
                                                            viewerMemberId={memberId || undefined}
                                                            createAlert={isReadOnly || isCenterViewer ? undefined : createAlert}
                                                        />
                                                    </Col>
                                                )}

                                                {/* 하단 액션 */}
                                                <Row style={{ gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={() => setSelectedSubscription(null)}
                                                        style={{
                                                            padding: '8px 20px', borderRadius: 8, fontSize: 15,
                                                            color: '#6B7280', backgroundColor: '#F3F4F6',
                                                            border: 'none', cursor: 'pointer',
                                                        }}
                                                    >닫기</button>
                                                </Row>
                                            </Col>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* ──── PT 탭 ──── */}
                        {activeTab === 'pt' && (
                            <PTTab
                                ptInfo={ptInfo}
                                memberId={memberId as string}
                                memberName={member?.name}
                                centerId={centerId}
                                dropDownCoachList={dropDownCoachList}
                                createAlert={createAlert}
                                createTwoButtonAlert={createTwoButtonAlert}
                                isReadOnly={isReadOnly}
                            />
                        )}

                        {/* ──── 그룹 수업 탭 ──── */}
                        {activeTab === 'group' && centerId && memberId && (
                            <MemberGroupTab
                                centerId={centerId}
                                memberId={memberId}
                                memberName={member?.name}
                                createAlert={createAlert}
                                isReadOnly={isReadOnly}
                            />
                        )}

                        {/* ──── 출석 기록 탭 ──── */}
                        {activeTab === 'checkin' && (
                            <>
                                {checkins.length > 0 ? (
                                    <Grid
                                        colDefs={checkinColDefs}
                                        rowData={checkins}
                                        leftTopCustomComponent={() => (
                                            <CaptionLight color={colors.LAVEL_1}>{`출석 ${checkins.length}건`}</CaptionLight>
                                        )}
                                    />
                                ) : (
                                    renderPanelEmptyState('📋', '출석 기록이 없습니다', '아직 체크인 이력이 없어 표시할 출석 데이터가 없습니다.')
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Col>

            {/*
              삭제 모달 — U3.
              회원을 삭제하면 그 회원의 수강권이 종료(소프트 삭제)된다. 지금까지는 수강권 상세가
              404 라 "삭제하면 매출을 못 고친다"가 사실이었는데, 상세가 열리면서 그 제약이
              사라졌으므로 안내도 함께 바꾼다.
              탈퇴 회원은 한 가지가 더 있다 — 원매출 환불은 열렸지만 그렇게 만든 환불 내역은
              이후 수정·삭제할 수 없다. 누르기 전에 알려야 한다.
            */}
            {(!isReadOnly || canRemovePreservedMember) && isDeleteVisible && (
                <ModalAlert
                    modalVisible={isDeleteVisible}
                    setModalVisible={setIsDeleteVisible}
                    title={canRemovePreservedMember ? '탈퇴 회원 삭제' : '회원 삭제'}
                    content={
                        canRemovePreservedMember
                            ? `'${member?.name}' 회원을 센터 목록에서 삭제하시겠습니까?\n과거 수업·매출 기록은 유지됩니다.\n\n탈퇴 회원은 원매출 환불만 가능합니다. 환불 내역은 이후 수정·삭제할 수 없으니 금액과 회차를 확인해 주세요.`
                            : `'${member?.name}' 회원을 삭제하시겠습니까?\n삭제해도 매출·환불 기록은 유지되며, 매출 정정과 원매출 환불은 계속 가능합니다.`
                    }
                    cancelButtonText="취소"
                    confirmButtonText="삭제"
                    callBack={handleDelete}
                    cancelCallBack={() => setIsDeleteVisible(false)}
                />
            )}

            {/* 이용권 삭제 확인 모달 */}
            {!isReadOnly && deleteSubTarget && (
                <ModalAlert
                    modalVisible={!!deleteSubTarget}
                    setModalVisible={() => setDeleteSubTarget(null)}
                    title={deleteSubTarget.isRefunded ? '환불 내역 삭제' : '이용권 삭제'}
                    content={deleteSubTarget.isRefunded
                        ? `"${deleteSubTarget.sub.productName}" 환불 내역을 삭제하시겠습니까?`
                        : `"${deleteSubTarget.sub.productName}" 이용권을 삭제하시겠습니까?`}
                    cancelButtonText="취소"
                    confirmButtonText="삭제"
                    callBack={async () => {
                        const target = deleteSubTarget;
                        setDeleteSubTarget(null);
                        try {
                            await deleteMutationSub.mutateAsync({
                                centerId,
                                subscriptionId: target.sub._id,
                                memberId: memberId || undefined,
                            });
                            createAlert('삭제 완료', target.isRefunded ? '환불 내역이 삭제되었습니다.' : '이용권이 삭제되었습니다.', false);
                        } catch {
                            createAlert('삭제 실패', '삭제에 실패했습니다.', true);
                        }
                    }}
                    cancelCallBack={() => setDeleteSubTarget(null)}
                />
            )}

            {/* 이용권 등록 모달 */}
            {!isReadOnly && (
                <SubscriptionRegisterModal
                    isVisible={isSubscriptionModalVisible}
                    onClose={() => setIsSubscriptionModalVisible(false)}
                    centerId={centerId}
                    memberId={memberId || ''}
                    createAlert={createAlert}
                />
            )}

            {/* 환불 모달 */}
            {!isReadOnly && (
                <SubscriptionRefundModal
                    isVisible={!!refundTarget}
                    onClose={() => setRefundTarget(null)}
                    centerId={centerId}
                    memberId={memberId || ''}
                    subscriptionId={refundTarget?._id || ''}
                    subscriptionPrice={refundTarget?.price}
                    createAlert={createAlert}
                    subscription={refundTarget}
                />
            )}

            {/* 수정 모달 */}
            {!isReadOnly && editTarget && (
                <SubscriptionEditModal
                    isVisible={!!editTarget}
                    onClose={() => setEditTarget(null)}
                    centerId={centerId}
                    memberId={memberId || ''}
                    subscription={editTarget}
                    createAlert={createAlert}
                    onTransfer={
                        isCenterViewer
                            ? undefined
                            : () => {
                                  setTransferTarget(editTarget);
                                  setEditTarget(null);
                              }
                    }
                />
            )}

            {/* 이용권 양도 */}
            {!isReadOnly && !isCenterViewer && transferTarget && (
                <SubscriptionTransferModal
                    centerId={centerId}
                    subscription={transferTarget}
                    sourceMemberName={member?.name || ''}
                    onClose={() => setTransferTarget(null)}
                    onDone={(result, recipientName) => {
                        setTransferTarget(null);
                        createAlert('양도 완료', buildTransferDoneMessage(result, recipientName), false);
                    }}
                />
            )}
            {isTransferHistoryVisible && memberId && (
                <SubscriptionTransferHistoryModal
                    centerId={centerId}
                    memberId={memberId}
                    memberName={member?.name}
                    onClose={() => setIsTransferHistoryVisible(false)}
                    createAlert={isReadOnly || isCenterViewer ? undefined : createAlert}
                />
            )}
            {isVisibleScheduleDetail && (
                <ScheduleDetailModal
                    modalVisible={isVisibleScheduleDetail}
                    setModalVisible={setIsVisibleScheduleDetail}
                    detailObject={detailObject}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                    calendarType={'daily'}
                    calendarSelectedDay={undefined}
                    isReadOnly={isReadOnly}
                />
            )}

            {/* 그룹 세션 정보 popover (수업 기록 group row 클릭) */}
            {groupSessionPopoverId && (
                <GroupSessionDetailPopover
                    sessionId={groupSessionPopoverId}
                    onClose={() => setGroupSessionPopoverId(null)}
                    createAlert={createAlert}
                />
            )}

            {/* 회원 정보 수정 모달 */}
            {!isReadOnly && isEditModalOpen && (
                <Modal
                    isCloseButton
                    setIsVisible={setIsEditModalOpen}
                    whiteViewWidth={480}
                    // 주소 입력란(약 70px)이 붙으면 고정 높이 안에서 스크롤이 생기므로 그만큼 늘린다.
                    whiteViewHeight={isAddressSupported ? 640 : 560}
                >
                    <Col style={{ padding: '32px 28px' }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 24 }}>회원 정보 수정</span>
                        <Col className="gap-[16px]">
                            <Col className="gap-[6px]">
                                <Body2Regular color={colors.LAVEL_2}>이름</Body2Regular>
                                <input style={inputStyle} value={editName} onChange={(e) => setEditName(e.target.value)} />
                            </Col>
                            <Col className="gap-[6px]">
                                <Body2Regular color={colors.LAVEL_2}>휴대폰 번호</Body2Regular>
                                <input style={inputStyle} value={editPhone} onChange={(e) => setEditPhone(formatKoreanPhoneNumberInput(e.target.value))} maxLength={13} />
                            </Col>
                            <Col className="gap-[6px]">
                                <Body2Regular color={colors.LAVEL_2}>성별</Body2Regular>
                                <Row className="gap-[12px]">
                                    {[{ code: 'male' as const, label: '남성' }, { code: 'female' as const, label: '여성' }].map((opt) => {
                                        const sel = editGender === opt.code;
                                        return (
                                            <button
                                                key={opt.code}
                                                style={{
                                                    flex: 1,
                                                    padding: '10px 0',
                                                    borderRadius: 8,
                                                    border: sel ? '1.5px solid #3B82F6' : '1px solid #E1E4E6',
                                                    backgroundColor: sel ? '#EFF6FF' : 'white',
                                                    color: sel ? '#2563EB' : '#6B7280',
                                                    fontSize: 14,
                                                    fontWeight: sel ? 600 : 400,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setEditGender(editGender === opt.code ? '' : opt.code)}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </Row>
                            </Col>
                            <Col className="gap-[6px]">
                                <Body2Regular color={colors.LAVEL_2}>생년월일</Body2Regular>
                                <input style={inputStyle} type="date" value={editBirthDate} onChange={(e) => setEditBirthDate(e.target.value)} />
                            </Col>
                            {isAddressSupported && (
                                <Col className="gap-[6px]">
                                    <Body2Regular color={colors.LAVEL_2}>주소</Body2Regular>
                                    <input style={inputStyle} value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
                                </Col>
                            )}
                            <Col className="gap-[6px]">
                                <Body2Regular color={colors.LAVEL_2}>메모</Body2Regular>
                                <textarea
                                    style={{ ...inputStyle, resize: 'none' }}
                                    rows={3}
                                    value={editMemo}
                                    onChange={(e) => setEditMemo(e.target.value)}
                                />
                            </Col>
                        </Col>
                        <Row className="gap-[12px] justify-end" style={{ marginTop: 24 }}>
                            <WhiteSquareButton text="취소" size="small" callBack={() => setIsEditModalOpen(false)} />
                            <ColorSquareButton text="저장" size="small" isDisabled={updateMutation.isLoading} callBack={handleUpdate} />
                        </Row>
                    </Col>
                </Modal>
            )}

            {/* 메모 모달 */}
            {isMemoModalOpen && (
                <Modal
                    isCloseButton
                    setIsVisible={setIsMemoModalOpen}
                    whiteViewWidth={480}
                    whiteViewHeight={340}
                >
                    <Col style={{ padding: '32px 28px' }}>
                        <Row className="items-center gap-[8px]" style={{ marginBottom: 20 }}>
                            <span style={{ fontSize: 20 }}>📝</span>
                            <span style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>회원 메모</span>
                        </Row>
                        <textarea
                            style={{
                                ...inputStyle,
                                height: 160,
                                resize: 'none',
                                backgroundColor: '#F8FAFC',
                                border: '1px solid #E2E8F0',
                            }}
                            placeholder="회원에 대한 메모를 입력해주세요 (ex: 부상 이력, 특이사항 등)"
                            value={editMemo}
                            onChange={(e) => setEditMemo(e.target.value)}
                            readOnly={isReadOnly}
                        />
                        <Row className="gap-[12px] justify-end" style={{ marginTop: 24 }}>
                            <WhiteSquareButton text={isReadOnly ? '닫기' : '취소'} size="small" callBack={() => setIsMemoModalOpen(false)} />
                            {!isReadOnly && <ColorSquareButton text="저장" size="small" isDisabled={updateMutation.isLoading} callBack={handleMemoUpdate} />}
                        </Row>
                    </Col>
                </Modal>
            )}

            {/* 담당강사 변경 모달 */}
            {!isReadOnly && isMergeModalOpen && (
                <MemberMergeModal
                    onClose={() => setIsMergeModalOpen(false)}
                    tempMemberId={memberId || ''}
                    tempMemberName={member?.name || ''}
                    tempPhoneNumber={formatKoreanPhoneNumberInput(member?.phoneNumber)}
                    sourceType={(member?.memberType as 'temp' | 'public') || 'temp'}
                    coachId={primaryRelation?.coachId}
                    coachName={primaryRelation?.coachName || primaryRelation?.alias}
                    onMergeComplete={(originalId) => {
                        setIsMergeModalOpen(false);
                        createAlert('병합 완료', '회원 병합이 완료되었습니다. 연동 회원 페이지로 이동합니다.', false);
                        navigate(`/publicMember/${originalId}`);
                    }}
                />
            )}

            {!isReadOnly && isChangeCoachModal && (
                <ModalMemberProfileComponentCoachChange
                    setIsVisible={setIsChangeCoachModal}
                    dropDownCoachList={dropDownCoachList}
                    selectCoach={
                        dropDownCoachList.find((c: CodeValueType) => c.code === primaryRelation?.coachId) ??
                        assignedCoachOptions.find((c) => c.code === primaryRelation?.coachId)
                    }
                    sourceCoachList={assignedCoachOptions}
                    failure={changeCoachFailure?.failure}
                    isSubmitting={
                        patchChangeCoachForMemberMutation.isLoading || postAssignCoachMutation.isLoading
                    }
                    inquiryInfo={
                        changeCoachFailure
                            ? {
                                  memberName: member?.name || '이름 미확인',
                                  memberId: memberId || '확인 불가',
                                  centerId: centerId || '확인 불가',
                                  occurredAt: dayjs(changeCoachFailure.occurredAt).format('YYYY-MM-DD HH:mm:ss'),
                                  errorCode: getChangeCoachErrorCode(changeCoachFailure.failure),
                              }
                            : undefined
                    }
                    onRefresh={() => {
                        void refetch().finally(() => {
                            setChangeCoachFailure(null);
                            setIsChangeCoachModal(false);
                        });
                    }}
                    onViewMemberships={() => {
                        setChangeCoachFailure(null);
                        setIsChangeCoachModal(false);
                        setActiveTab('pt');
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                document.getElementById('member-detail-tab-pt')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                });
                            });
                        });
                    }}
                    onClickChangeCoach={(newCoachId: string, sourceCoachId?: string) => {
                        if (
                            patchChangeCoachForMemberMutation.isLoading ||
                            postAssignCoachMutation.isLoading
                        ) {
                            return;
                        }
                        setChangeCoachFailure(null);
                        const coachId = sourceCoachId || primaryRelation?.coachId;
                        if (coachId) {
                            patchChangeCoachForMemberMutation.mutate({
                                centerId: centerId as string,
                                memberId: memberId as string,
                                coachId,
                                newCoachId: newCoachId,
                            });
                        } else {
                            postAssignCoachMutation.mutate({
                                centerId: centerId as string,
                                memberId: memberId as string,
                                coachId: newCoachId,
                            });
                        }
                    }}
                />
            )}

            {/* 프로필 상단의 빠른 강사 추가 배정 */}
            {!isReadOnly && isAddCoachModal && (
                <Modal
                    isCloseButton
                    isCloseOutside={!addCoachMutation.isLoading}
                    onClose={closeAddCoachModal}
                    whiteViewWidth={480}
                    whiteViewHeight={330}
                >
                    <Col style={{ padding: '36px 32px' }}>
                        <span style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>강사 추가 배정</span>
                        <span style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: '#6B7280' }}>
                            {member?.name || '회원'}님에게 추가로 배정할 강사를 선택해주세요.
                        </span>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 24 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>배정할 강사</span>
                            <select
                                aria-label="배정할 강사"
                                value={coachIdToAdd}
                                onChange={(event) => setCoachIdToAdd(event.target.value)}
                                disabled={addCoachMutation.isLoading}
                                style={{ ...inputStyle, cursor: addCoachMutation.isLoading ? 'not-allowed' : 'pointer' }}
                            >
                                <option value="">강사를 선택해주세요</option>
                                {availableCoachOptions.map((coach) => (
                                    <option key={coach.code} value={coach.code}>{coach.value}</option>
                                ))}
                            </select>
                        </label>
                        <Row className="gap-[12px] justify-end" style={{ marginTop: 32 }}>
                            <WhiteSquareButton
                                text="취소"
                                size="small"
                                isDisabled={addCoachMutation.isLoading}
                                callBack={closeAddCoachModal}
                            />
                            <ColorSquareButton
                                text={addCoachMutation.isLoading ? '배정 중...' : '배정하기'}
                                size="small"
                                isDisabled={!coachIdToAdd || addCoachMutation.isLoading}
                                callBack={handleAddCoach}
                            />
                        </Row>
                    </Col>
                </Modal>
            )}

            {!isReadOnly && isTicketTransferModal && centerId && memberId && (
                <ModalTicketTransfer
                    centerId={centerId}
                    memberId={memberId}
                    memberName={member?.name}
                    setIsVisible={setIsTicketTransferModal}
                    createAlert={createAlert}
                    coachOptions={dropDownCoachList}
                    onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(centerId, memberId) });
                    }}
                />
            )}

        </Col>
    );
};

export default PublicMemberDetailPage;
